import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { AdaptiveTabs, AdaptiveTabItem, useAdaptiveTab } from '@components/common/AdaptiveTabs.tsx';
import { PageIndices, TabIndices, ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { EhiSnapshotsTab } from '@components/admin/ehi/EhiSnapshotsTab.tsx';
import { EhiSummariesTab } from '@components/admin/ehi/EhiSummariesTab.tsx';
import { toastService } from '@services/toast.service.ts';
import { getErrorMessage } from '@/utils/api-response.util';
import api from '@services/api.ts';
import { calculateMetric } from '@services/calculated-metrics.service.ts';
import clientScenariosData from './health-index-client-scenarios.json';
import {
  BaselineStatusRow,
  CalculateResponse,
  HealthIndexCalculatorName,
  HealthIndexConfig,
  IndexLevelRow,
  calculateHealthIndex,
  getCalculatorInfo,
} from '@services/health-index.service.ts';

type DirectionKey = keyof HealthIndexConfig['sixDirectionsMatrix'];
const DIRECTIONS: DirectionKey[] = [
  'physical',
  'energy',
  'clarity',
  'productivity',
  'focus',
  'emotional',
];
const DIRECTION_LABELS: Record<string, string> = {
  physical: 'Фізичний стан',
  energy: 'Енергія',
  clarity: 'Ясність',
  productivity: 'Продуктивність',
  focus: 'Фокус',
  emotional: 'Емоційний стан',
};
const BLOCKS: (keyof HealthIndexConfig['sixDirectionsMatrix']['physical'])[] = ['A', 'F', 'T', 'N', 'W'];
const BLOCK_LABELS: Record<string, string> = { A: 'Активність', F: 'Відчуття', T: 'Задачі', N: 'Харчування', W: 'Вода' };

const CALCULATOR_TITLES: Record<HealthIndexCalculatorName, string> = {
  'baseline-index': 'Стартовий Індекс',
  'daily-resource': 'Ресурс дня',
  'rolling-index': "Індекс за 30 днів",
};

const LEVEL_COLOR: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
  low: 'error',
  unstable: 'warning',
  good: 'info',
  strong: 'success',
};

type ParamColor = 'info' | 'success' | 'warning' | 'secondary' | 'primary' | 'default';

// Кольорове групування (2026-08-21, за проханням користувача - "щоби візуально орієнтуватися"):
// споріднені параметри отримують той самий колір, що й блок, який вони формують. W/N/A -
// самостійні блоки (кожен свій колір); Q/M і похідний T - один колір (secondary); U/S і похідний
// F - один колір (primary). Лише MUI system colors (web/CLAUDE.md - без хардкоду кольорів).

// Однозначний неймінг (2026-08-21, за проханням користувача): кожне поле форми показує ТОЧНУ
// назву змінної з документа (spec-condensed-v1.3.md розділ 2) - `fact_water`, `state_rating`
// тощо - не довільний переклад/скорочення API. `required` - зі списку `requiredParams`, який
// віддає GET /health-index/:name/info (той самий масив, що бек використовує для власної
// документації) - не окремий hardcode, який міг би розійтись з реальним контрактом. `color` -
// чисто візуальне групування спорідненості (MUI system color, без хардкоду).

// Цитати з ОРИГІНАЛУ ТЗ (2026-08-21, за проханням користувача - "звірити зі специфікацією",
// приміти з цитатами на вкладках "Вхідні дані"/"Конфігуратор"). Джерело — не дистиляція
// spec-condensed-v1.3.md, а сам первинний файл продукту, рядки перевірені grep'ом.
const SPEC_SOURCE_FILE = 'Everyday_EHI_product_spec_v1.3_2026-08-12.docx.md';

const SPEC_QUOTES: Record<string, { quote: string; line: number; file?: string }> = {
  W: { quote: 'W = min(100, fact_water / target_water × 100)', line: 171 },
  N: {
    quote:
      'x = fact_kcal / target_kcal. Якщо 0,90 ≤ x ≤ 1,10, N = 100; якщо 0,50 < x < 0,90, N = 100 × (x − 0,50) / 0,40; ' +
      'якщо 1,10 < x < 1,50, N = 100 × (1,50 − x) / 0,40; якщо x ≤ 0,50 або x ≥ 1,50, N = 0.',
    line: 177,
  },
  A: { quote: 'A = min(100, fact_active_kcal / target_active_kcal × 100)', line: 183 },
  Q: { quote: 'Q = completed_priority_tasks / planned_priority_tasks × 100, якщо planned_priority_tasks > 0; інакше Q = N/A', line: 189 },
  M: { quote: 'M = completed_doses / planned_doses × 100, якщо planned_doses > 0 у цей день; інакше M = N/A', line: 193 },
  T: {
    quote:
      'T = (Q + M) / 2, якщо застосовуються Q і M; T = Q, якщо застосовується лише Q; T = M, якщо застосовується ' +
      'лише M; T = N/A, якщо не застосовується жоден компонент',
    line: 197,
  },
  U: { quote: 'U = (state_rating − 1) × 25, де state_rating ∈ {1, 2, 3, 4, 5}; якщо оцінку не внесено до завершення дня, U = 0', line: 205 },
  S: {
    quote:
      'y = fact_sleep_hours / target_sleep_hours. Якщо 0,90 ≤ y ≤ 1,10, S = 100; якщо 0,50 < y < 0,90, S = 100 × (y − 0,50) / 0,40; ' +
      'якщо 1,10 < y < 1,50, S = 100 × (1,50 − y) / 0,40; якщо y ≤ 0,50 або y ≥ 1,50, S = 0.',
    line: 209,
  },
  dailyResources: { quote: 'EHI_t = (R_t + R_(t−1) + … + R_(t−29)) / 30', line: 247 },
  rollingUpdate: { quote: 'EHI_new = EHI_old + (R_new − R_oldest) / 30', line: 261 },
  selfAssessment: {
    quote: 'Під час онбордингу людина напряму оцінює шість напрямків від 1 до 5. Вони переводяться у 0, 25, 50, 75, 100 і формують Стартовий Індекс',
    line: 280,
  },
  completedResources: { quote: 'EHI_n = ((30 − n) × EHI_start + Σ R_d) / 30, де 0 ≤ n ≤ 30', line: 288 },
  physical: { quote: '0,35A + 0,30N + 0,15W + 0,10F + 0,10T', line: 223 },
  energy: { quote: '0,25A + 0,25W + 0,20N + 0,20F + 0,10T', line: 224 },
  clarity: { quote: '0,30F + 0,30W + 0,15A + 0,15N + 0,10T', line: 225 },
  productivity: { quote: '0,40T + 0,25A + 0,15F + 0,10N + 0,10W', line: 226 },
  focus: { quote: '0,35T + 0,25W + 0,25N + 0,10F + 0,05A', line: 227 },
  emotional: { quote: '0,35F + 0,20N + 0,15A + 0,15T + 0,15W', line: 228 },
  sixDirectionsMatrix: {
    quote:
      'Коефіцієнти збалансовані: сумарна вага кожного з п\'яти блоків у шести формулах однакова — 1,20. Конкретний ' +
      'розподіл ваг між напрямками є стартовою продуктовою гіпотезою Everyday для MVP, а не науково або клінічно ' +
      'підтвердженою моделлю.',
    line: 219,
  },
  indexLevels: {
    quote: '0–39 Низький; 40–59 Нестабільний; 60–79 Добрий; 80–100 Сильний',
    line: 267,
  },
  ratingScale: {
    quote: 'Вони переводяться у 0, 25, 50, 75, 100 і формують Стартовий Індекс',
    line: 280,
  },
  baselineStatuses: {
    quote:
      'До першого завершеного дня — «Стартовий Індекс — за результатами опитування»; 1–6 днів — «Індекс формується — ' +
      'додано n із 30 днів»; 7–29 днів — «Попередній Індекс — додано n із 30 днів»; 30 днів і більше — «Індекс ' +
      'здоров\'я Everyday — останні 30 днів»',
    line: 292,
  },
  corridor: {
    quote: 'x = fact_kcal / target_kcal. Якщо 0,90 ≤ x ≤ 1,10, N = 100 ... (симетрична формула, та сама логіка застосована й до сну: S)',
    line: 177,
  },
  // (2026-08-21) Ці 6 ключів раніше цитували ТЗ v1.3 розділ 11.1 (стара послідовність
  // онбордингу: профіль → мета → рейтинги). Продакт дав нову задачу (task.md), яка ПЕРЕСТАВЛЯЄ
  // порядок (рейтинги й Стартовий Індекс — одразу першими, профіль/мета — окрема пізніша фаза,
  // Phase 2) — тепер цитують саме її, бо для флоу симуляції вона актуальніша. Стара ТЗ-125
  // цитата (розділ 11.1) лишається правильною для ФОРМУЛ (де в цих кроків жодних змін не було),
  // просто вже не для порядку показу.
  gender: { quote: '2. Стать', line: 146, file: 'workspace/current-work/onbording/task.md' },
  age: { quote: '3. Вік', line: 147, file: 'workspace/current-work/onbording/task.md' },
  height: { quote: '4. Зріст', line: 148, file: 'workspace/current-work/onbording/task.md' },
  weight: { quote: '5. Вага', line: 149, file: 'workspace/current-work/onbording/task.md' },
  activityLevel: {
    quote: '6. Рівень фізичної активності — малорухливий спосіб життя; легко активний; помірно активний; дуже активний.',
    line: 150,
    file: 'workspace/current-work/onbording/task.md',
  },
  goal: {
    quote:
      '1. Основна ціль — зниження ваги; підтримка здоров\'я; покращення самопочуття; нарощування м\'язів; витривалість; ' +
      'набір ваги. (⚠️ на відміну від Фокусу й Улюблених активностей нижче, тут НЕ вказано single/multi-select — ' +
      'мок обрав single, як відкрите питання до продакту)',
    line: 139,
    file: 'workspace/current-work/onbording/task.md',
  },
  phase2ValueMessage: {
    quote:
      'Перед блоком показати пояснення: «Сформуємо план під вашу ціль» — Спочатку оберіть, чого хочете досягти. Після ' +
      'цього додайте кілька базових даних — вони допоможуть Everyday розрахувати персональні цілі щодо харчування, ' +
      'води та активності.',
    line: 131,
    file: 'workspace/current-work/onbording/task.md',
  },
  phase0: {
    quote:
      '0. Intro → Disclaimer → Registration. Залишити існуючі screens: Intro → «Індекс здоров\'я — не медичний ' +
      'діагноз» → Login / Registration. Progressive onboarding стартує після успішної реєстрації нового користувача.',
    line: 44,
    file: 'workspace/current-work/onbording/task.md',
  },
  phase1ValueMessage: {
    quote:
      'Перед питаннями показати короткий value message: «Дізнайтесь свою стартову точку» — Відповідайте на кілька ' +
      'коротких запитань про ваш поточний стан. На основі відповідей Everyday сформує ваш перший Health Index і ' +
      'покаже, які сфери зараз потребують найбільше уваги.',
    line: 62,
    file: 'workspace/current-work/onbording/task.md',
  },
  focusSelect: {
    quote:
      'На якій сфері життя ви хочете зосередитися зараз? Single select: Тіло; Гроші; Кар\'єра або бізнес; ' +
      'Самореалізація; Стосунки; Сім\'я; Діти; Емоційний стан; Оточення.',
    line: 109,
    file: 'workspace/current-work/onbording/task.md',
  },
  q_physical: { quote: '«Я почуваюся фізично добре та не відчуваю суттєвого дискомфорту.»', line: 73, file: 'workspace/current-work/onbording/task.md' },
  q_energy: { quote: '«У мене достатньо енергії протягом дня.»', line: 75, file: 'workspace/current-work/onbording/task.md' },
  q_clarity: { quote: '«Я чітко розумію, що мені потрібно робити для досягнення своїх цілей.»', line: 77, file: 'workspace/current-work/onbording/task.md' },
  q_productivity: { quote: '«Мені завжди вдається виконувати заплановані справи.»', line: 79, file: 'workspace/current-work/onbording/task.md' },
  q_focus: { quote: '«Мені вдається довго утримувати увагу на важливих справах.»', line: 81, file: 'workspace/current-work/onbording/task.md' },
  q_emotional: { quote: '«Я почуваюся емоційно стабільно та спокійно.»', line: 83, file: 'workspace/current-work/onbording/task.md' },
  personalPlan: {
    quote:
      'Дані можуть включати: калорії; Б/Ж/В; норму води; активність; задачі; відчуття... Important: Personal Plan ' +
      'не показувати як повністю персоналізований до завершення required-полів Phase 2.',
    line: 162,
    file: 'workspace/current-work/onbording/task.md',
  },
  phase3List: {
    quote: 'Дані Phase 3: сон; препарати; додаткові цілі; улюблені активності; додаткова антропометрія; інші дані, які підвищують персоналізацію.',
    line: 189,
    file: 'workspace/current-work/onbording/task.md',
  },
  favoriteActivities: {
    quote: 'Улюблені активності. Multi-select, наприклад: прогулянки; велосипед; біг; плавання; тренажерний зал; йога; танці; інше.',
    line: 198,
    file: 'workspace/current-work/onbording/task.md',
  },
  phase4List: {
    quote:
      'PHASE 4 — Optional / contextual. Не включати в обов\'язковий onboarding. Запитувати лише в релевантному місці ' +
      'продукту: Apple Health → при підключенні джерел; додаткова антропометрія → у відповідному розділі; інші ' +
      'health-параметри → коли вони потрібні конкретній функції.',
    line: 240,
    file: 'workspace/current-work/onbording/task.md',
  },
};

const SpecQuote: React.FC<{ quoteKey: string }> = ({ quoteKey }) => {
  const entry = SPEC_QUOTES[quoteKey];
  if (!entry) return null;
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ maxWidth: 320, p: 0.5 }}>
          <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}>
            «{entry.quote}»
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {entry.file ?? SPEC_SOURCE_FILE}, рядок {entry.line}
          </Typography>
        </Box>
      }
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle' }} />
    </Tooltip>
  );
};

// Підпис реального Figma-екрана (2026-08-20, за проханням користувача — "всі нейми сторінок
// дизайну… давай зробимо щоби виділялися. додай заливку, можеш ще іконку додати щоби було видно,
// що це конкретна сторінка") — заливка + іконка екрана, замість голого monospace-тексту, щоб
// `page:onboarding:...` одразу читався як "назва конкретного екрана", не як службовий текст.
const PageNameTag: React.FC<{ name: string; sx?: object }> = ({ name, sx }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      px: 0.75,
      py: 0.25,
      ...sx,
    }}
  >
    <PhoneIphoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
      {name}
    </Typography>
  </Box>
);

// Подвійне цитування (2026-08-21, за проханням користувача): де ми посилаємось на власну
// дистиляцію spec-condensed-v1.3.md, поруч завжди показуємо і пункт ОРИГІНАЛЬНОГО документа —
// у стейкхолдерів є лише оригінал, вони не мають доступу до внутрішньої технічної вижимки.
// Формат навмисно єдиний скрізь на сторінці (власний вибір, консенсусу з продуктом нема):
// {orig: "1.3", spec: "2.6"} → "ТЗ v1.3 п. 1.3 · spec-condensed-v1.3.md п. 2.6".
// `task` (2026-08-21, за проханням користувача — "можемо посилатися на таску як 3-тє джерело")
// — опційне 3-тє джерело, workspace/current-work/onbording/task.md (задача продакту з
// онбордингу). Опційне, бо не кожна цитата на сторінці стосується онбордингу.
const SourceRefs: React.FC<{ orig: string; spec: string; task?: string }> = ({ orig, spec, task }) => (
  <Typography component="span" variant="body2" color="text.secondary">
    Джерела: ТЗ v1.3 п. {orig} · spec-condensed-v1.3.md п. {spec}
    {task && <> · task.md п. {task}</>}
  </Typography>
);

const ParamLabel: React.FC<{ label: string; param: string; required?: boolean; color?: ParamColor; quoteKey?: string }> = ({
  label,
  param,
  required,
  color = 'default',
  quoteKey,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
    <Typography variant="subtitle2" component="span">
      {label}
      {required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
    </Typography>
    <Chip
      label={param}
      size="small"
      variant="outlined"
      color={color === 'default' ? undefined : color}
      sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 18, '& .MuiChip-label': { px: 0.75 } }}
    />
    {quoteKey && <SpecQuote quoteKey={quoteKey} />}
  </Box>
);

// Сторінка з давнішого ТЗ - частина полів конфігу зараз НЕ читається реальним конвеєром модуля ehi
// (лише калькуляторами-симуляторами тут же), а не видалена (рішення користувача 2026-09-23: "залиш на
// фронтенді але якось відділи візуально що воно ще не активне. ми поступово все актуалізуємо"). Пунктирна
// рамка + приглушений фон + підпис - редагування лишається робочим (значення все одно зберігаються в
// єдиному конфізі), просто видно, що на реальний розрахунок EHI це поки не впливає.
const NotActiveInEhi: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      border: '1px dashed',
      borderColor: 'warning.main',
      borderRadius: 1,
      p: 1.5,
      bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.08)' : 'rgba(237, 108, 2, 0.05)'),
    }}
  >
    {children}
    <Chip
      label="Ще не використовується в модулі ehi"
      size="small"
      color="warning"
      variant="outlined"
      sx={{ mt: 1, fontSize: '0.7rem', height: 20 }}
    />
  </Box>
);

function parseNumberList(raw: string): number[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map(Number);
}

type ResultState = { calculator: HealthIndexCalculatorName; response: CalculateResponse } | null;

// Знімок поточних значень 3 форм вкладки "Вхідні дані" — для другої кнопки анкети, яка
// друкує документ, уже заповнений тим, що зараз введено на фронтенді (жирним), а не порожній
// бланк. Форми не мають єдиного піднятого стану — тому кожна група віддає свій шматок через
// useImperativeHandle (DailyResourceGroupHandle/RollingIndexGroupHandle/BaselineIndexGroupHandle),
// а не через рефактор на контрольований стан батька.
type DailyResourceSnapshot = {
  water: { fact: number; target: number };
  nutrition: { fact: number; target: number };
  activity: { fact: number; target: number };
  tasks: { completed: number; planned: number };
  medicationActive: boolean;
  medication: { completed: number; planned: number };
  stateRatingEntered: boolean;
  stateRating: number;
  sleepActive: boolean;
  sleep: { factHours: number; targetHours: number };
};
type RollingIndexSnapshot = { dailyResources: number[] };
type BaselineIndexSnapshot = { selfAssessment: Record<DirectionKey, number>; completedResources: number[] };

// Дані, які клієнт нібито вводить під час онбордингу (кроки 8 і 10 — 6 оцінок стану + особисті
// цілі дня, spec-condensed-v1.3.md розділ 12 / оригінал ТЗ розділ 11.1) — іменовані сценарії CLIENT_SCENARIOS
// (2026-08-21). Виключно фронтенд-мок: вкладка "Симуляція" лише показує дані (без дій), а на
// вкладці "Вхідні дані" ті самі сценарії вбудовані пілюлями в наявні ряди пресетів
// DailyResourceGroup/BaselineIndexGroup — один спільний список з "рандомними" пресетами, не
// окремий механізм.
type OnboardingSnapshot = {
  selfAssessment: Record<DirectionKey, number>;
  water: number;
  nutrition: number;
  // Б/Ж/В (2026-08-20) — знайдено на реальному скріні `page:onboarding:personal plan`
  // (користувач надав PNG): картка "Ціль по харчуванню" показує калорії + рядок Білки/Вуглеводи/
  // Жири. Раніше помилково вважалось, що в дизайні цього немає — переплутано з
  // lorem-плейсхолдер-текстом у CSS-експорті ("10 ноября, 16:21" в незаповнених текстових шарах),
  // не звірено зі скріном. Ілюстративні числа, не результат реального AMDR-розрахунку.
  proteinG: number;
  carbsG: number;
  fatG: number;
  activity: number;
  sleepActive: boolean;
  sleepTargetHours: number;
  medicationActive: boolean;
  medicationPlanned: number;
  tasksPlanned: number;
};

// Профіль = PHASE 2 задачі продакту (workspace/current-work/onbording/task.md) — поля, які
// реально годують calculated-metrics (BMR/AMR/WDNA: `gender`/`age`/`height`/`weight`/
// `activityLevel` — точні назви звідти, `api/src/modules/calculated-metrics/calculators/
// {bmr,amr,wdna}.calculator.ts`). Розбіжність, яку СВІДОМО не згладжуємо: ці калькулятори мають
// 5-рівневий `activityLevel` (+ `extra_active`), а задача продакту (Phase 2, крок 6) визначає
// лише 4 рівні — тому тип лишає 5 значень (сумісність з калькулятором), але селект нижче
// пропонує тільки 4, як у задачі. 2026-08-21, за проханням користувача — редаговані інпути, не
// read-only показ (на відміну від решти фаз нижче).
// 'unspecified' (2026-08-20, підтверджено реальним Figma-екраном page:onboarding:sex — третій
// варіант "Вважаю за краще не відповідати") — calculated-metrics (WDNA/BMR `GENDER_COEFFICIENTS`)
// рахує коефіцієнт лише для male/female. Рішення стейкхолдерів (2026-08-20): підставляти
// female — див. Phase2ResultCard (effectiveGender) і api/docs/technical-debt.md (пункт 13,
// правильне місце для цієї логіки — самі калькулятори, не UI-виклик).
type Gender = 'male' | 'female' | 'unspecified';
type ActivityLevel = 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active' | 'extra_active';
// Лише 4 з 5 значень ActivityLevel — рівно ті, що є в задачі продакту (Phase 2, крок 6); "дуже
// висока активність" (extra_active) з calculated-metrics в онбординг не потрапляє.
const ONBOARDING_ACTIVITY_LEVELS: ActivityLevel[] = ['sedentary', 'lightly_active', 'moderately_active', 'very_active'];
type Goal = 'weight_loss' | 'maintenance' | 'wellbeing' | 'muscle_gain' | 'endurance' | 'weight_gain';

type ClientProfile = {
  gender: Gender;
  age: number;
  height: number;
  weight: number;
  activityLevel: ActivityLevel;
  goal: Goal;
};

// Лейбли — дослівно з реального Figma-екрана page:onboarding:sex (2026-08-20), не з задачі
// (task.md крок 2 просто каже "Стать", варіантів не перелічує).
const GENDER_LABELS: Record<Gender, string> = {
  male: 'Чоловіча',
  female: 'Жіноча',
  unspecified: 'Вважаю за краще не відповідати',
};

// Лейбли — дослівно з задачі продакту (Phase 2, крок 6), а не з calculated-metrics.
const ACTIVITY_LEVEL_LABELS: Record<ActivityLevel, string> = {
  sedentary: 'малорухливий спосіб життя',
  lightly_active: 'легко активний',
  moderately_active: 'помірно активний',
  very_active: 'дуже активний',
  extra_active: 'дуже висока активність (лише calculated-metrics, немає в онбордингу)',
};

// Лейбли — дослівно з задачі продакту (Phase 2, крок 1).
const GOAL_LABELS: Record<Goal, string> = {
  weight_loss: 'зниження ваги',
  maintenance: 'підтримка здоров\'я',
  wellbeing: 'покращення самопочуття',
  muscle_gain: 'нарощування м\'язів',
  endurance: 'витривалість',
  weight_gain: 'набір ваги',
};

// Валідація форми елементів масиву при JSON-імпорті в конфігураторі симулянтів (2026-08-20,
// кодревью — `applyJsonDraft` перевіряв лише Array.isArray + довжину, тому битий елемент (напр.
// `null` або об'єкт без `goal`) приймався й валив рендер ProfileCard/Phase2ResultCard далі
// `TypeError`-ом на прямому зверненні до `profile.goal`/`GENDER_LABELS[profile.gender]` тощо).
function validateClientProfile(value: unknown, index: number): ClientProfile {
  if (typeof value !== 'object' || value === null) {
    throw new Error(`Профіль #${index + 1}: очікується об'єкт, отримано ${JSON.stringify(value)}`);
  }
  const p = value as Record<string, unknown>;
  if (typeof p.gender !== 'string' || !(p.gender in GENDER_LABELS)) {
    throw new Error(`Профіль #${index + 1}: поле "gender" має бути одним із ${Object.keys(GENDER_LABELS).join('/')}`);
  }
  if (typeof p.age !== 'number' || !Number.isFinite(p.age)) {
    throw new Error(`Профіль #${index + 1}: поле "age" має бути числом`);
  }
  if (typeof p.height !== 'number' || !Number.isFinite(p.height)) {
    throw new Error(`Профіль #${index + 1}: поле "height" має бути числом`);
  }
  if (typeof p.weight !== 'number' || !Number.isFinite(p.weight)) {
    throw new Error(`Профіль #${index + 1}: поле "weight" має бути числом`);
  }
  if (typeof p.activityLevel !== 'string' || !(p.activityLevel in ACTIVITY_LEVEL_LABELS)) {
    throw new Error(`Профіль #${index + 1}: поле "activityLevel" має бути одним із ${Object.keys(ACTIVITY_LEVEL_LABELS).join('/')}`);
  }
  if (typeof p.goal !== 'string' || !(p.goal in GOAL_LABELS)) {
    throw new Error(`Профіль #${index + 1}: поле "goal" має бути одним із ${Object.keys(GOAL_LABELS).join('/')}`);
  }
  return p as unknown as ClientProfile;
}

// PHASE 1.1 задачі продакту — "На якій сфері життя ви хочете зосередитися зараз?" (single select).
// FIX (2026-09-03, за прямою вказівкою користувача) — попередній набір з 5 варіантів помилково
// дублював 6 напрямків EHI (self-assessment), не був окремим словником "сфер життя". Замінено
// на 9 варіантів з тез продукту "[MVP/P4] Onboarding: Порядок показу фаз та питань".
// FIX (2026-09-09, Trello №50, п.3, синхронізовано з app/api OnboardingFocus): 9→6 —
// 'money'+'career_or_business' об'єднано в 'money_career', 'family'+'children' в
// 'family_children', 'environment' прибрано.
type FocusOption =
  | 'body'
  | 'money_career'
  | 'self_realization'
  | 'relationships'
  | 'family_children'
  | 'emotional_state';
const FOCUS_LABELS: Record<FocusOption, string> = {
  body: 'тіло',
  money_career: 'гроші, кар\'єра та бізнес',
  self_realization: 'самореалізація',
  relationships: 'стосунки',
  family_children: 'сім\'я та діти',
  emotional_state: 'емоційний стан',
};
// Порядок рядків Figma-екрана page:onboarding:focus:selected (node-id 2009-15869) — той самий
// порядок, у якому варіанти показані користувачу.
const FOCUS_OPTIONS: FocusOption[] = [
  'body',
  'money_career',
  'self_realization',
  'relationships',
  'family_children',
  'emotional_state',
];

// PHASE 3/4 задачі продакту — поза межами EHI-калькулятора взагалі, суто ілюстративні мок-дані
// для показу флоу стейкхолдерам (жодних розрахунків, жодних дій — те саме правило "тільки дані",
// що й для решти симуляції).
type Phase3Data = {
  // ДВА окремі поля (2026-08-20, перегляд рішення — "для сну треба просити одразу 2 показники —
  // факт і ціль", "ціль нам потрібна для розрахунків"): по аналогії з тим, як N/W уже влаштовані в
  // системі (ціль рахується/задається окремо від факту, не є "власним середнім" користувача —
  // WDNA рахує ціль води з ваги/статі/віку/активності, а не з поточного споживання). Раніше було
  // одне поле `sleepHoursPerDay`, трактоване як самозвіт-замінник цілі — цей підхід замінено.
  // Назви полів TS-об'єкта збігаються з реальними параметрами `daily-resource` калькулятора
  // (`sleep.factHours`/`sleep.targetHours`, api/src/modules/health-index/calculators/
  // daily-resource.calculator.ts) — АЛЕ показана в UI технічна назва відрізняється (2026-08-20,
  // поправка користувача — "goals. ти неправильно зробив неймінг"): `sleepTargetHours` —
  // повноцінна майбутня ціль (як `goals.waterMl`/`goals.main` у Phase2ResultCard), тому в UI
  // підписується `goals.sleepTargetHours`, а не `phase3.sleepTargetHours` — простір "goals."
  // означає "реальний майбутній вхід калькулятора", не "мок-дані цієї фази". `sleepFactHours` —
  // не ціль, лишається під `phase3.` І те, і те зараз лише записується в профіль, ніде не
  // розраховується (детальніше — workspace/current-work/ehi/sleep-open-questions.md, яку
  // це рішення частково закриває, але не повністю — п.6 про заміну відсутнього щоденного
  // факту лишається відкритим).
  sleepFactHours: number;
  sleepTargetHours: number;
  // Кожен препарат окремим рядком (2026-08-20, за проханням користувача) — вільний текст на
  // рядок ("Вітамін D, 1 раз/день"), НЕ структуровані qty/dosage. Формат узгоджений з реальним
  // AI-логуванням прийому препаратів у застосунку (rawInput → items[] у
  // `calendars/doc/medication.md`, варіант 5/6), але тут це лише онбординговий "графік", не
  // денний факт — і, як і сон, ніде не розраховується: реальний `computeT`/M
  // (`health-index.formulas.ts`) читає лише число `plannedDoses`, не назви препаратів.
  medications: string[];
  additionalGoals: string[];
  favoriteActivities: string[];
  // Список замість одного рядка (2026-08-20) — по аналогії з `BodyMeasurement` entity (api),
  // де кожен вимір зберігається окремо (typeCode), а не одним текстовим блобом; тут теж
  // "пропонуємо юзеру заповнити ще якісь параметри" — довільна кількість пар назва/значення.
  additionalAnthropometry: { label: string; value: string }[];
  otherData: string;
};

type Phase4Data = {
  appleHealthConnected: boolean;
  additionalAnthropometry: string;
  otherHealthParams: string;
};

type DailyResourceGroupHandle = { getSnapshot: () => DailyResourceSnapshot };
type RollingIndexGroupHandle = { getSnapshot: () => RollingIndexSnapshot };
type BaselineIndexGroupHandle = { getSnapshot: () => BaselineIndexSnapshot };

type QuestionnaireSnapshot = {
  baseline: BaselineIndexSnapshot;
  daily: DailyResourceSnapshot;
  rolling: RollingIndexSnapshot;
};

// Друкована анкета для офлайн-збору вхідних даних. Без `snapshot` — порожній бланк (щоб дати
// людині заповнити на папері). З `snapshot` — заповнена поточними значеннями форми, жирним
// (кнопка "з поточними даними (приклад)" на вкладці "Вхідні дані").
function buildQuestionnaireHtml(config: HealthIndexConfig, snapshot?: QuestionnaireSnapshot): string {
  const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const row = (label: string, unit = '', value?: string | number) => `
    <div class="field">
      <span class="field-label">${escape(label)}</span>
      <span class="field-line">${
        value !== undefined ? `<strong>${escape(String(value))}</strong>` : ''
      }${unit ? `<span class="unit">${escape(unit)}</span>` : ''}</span>
    </div>`;
  const checkboxRow = (label: string, checked?: boolean) => `
    <div class="field">
      <span class="checkbox">${checked === undefined ? '' : checked ? '✓' : ''}</span>
      <span>${checked !== undefined ? `<strong>${escape(label)}</strong>` : escape(label)}</span>
    </div>`;

  const ratingRows = DIRECTIONS.map((d) =>
    row(DIRECTION_LABELS[d], `${config.ratingScale.min}-${config.ratingScale.max}`, snapshot?.baseline.selfAssessment[d]),
  ).join('');

  // Заповнена анкета показує РІВНО стільки рядків днів, скільки зараз введено в
  // "Індекс за 30 днів" (не весь rollingWindowDays бланком) — це приклад поточних даних, не
  // порожній шаблон на все вікно.
  const dayRows = snapshot
    ? snapshot.rolling.dailyResources
        .map((v, i) => row(`День ${i + 1} (найновіший — останній)`, '0-100', v))
        .join('')
    : Array.from({ length: config.rollingWindowDays }, (_, i) => row(`День ${i + 1} (найновіший — останній)`, '0-100')).join('');

  return `<!doctype html>
<html lang="uk">
<head>
<meta charset="utf-8" />
<title>Everyday Health Index — анкета${snapshot ? ' (приклад із поточних даних)' : ' для заповнення'}</title>
<style>
  body { font-family: Arial, sans-serif; max-width: 800px; margin: 24px auto; color: #222; }
  h1 { font-size: 20px; }
  h2 { font-size: 16px; margin-top: 32px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  .hint { color: #666; font-size: 13px; margin-bottom: 8px; }
  .field { display: flex; align-items: baseline; gap: 12px; padding: 6px 0; border-bottom: 1px dotted #ccc; }
  .field-label { flex: 0 0 260px; }
  .field-line { flex: 1; border-bottom: 1px solid #333; min-height: 18px; display: flex; justify-content: flex-end; }
  .unit { color: #999; font-size: 12px; }
  .checkbox { display: inline-block; width: 14px; height: 14px; border: 1px solid #333; margin-right: 6px; vertical-align: middle; text-align: center; line-height: 14px; font-size: 11px; }
  strong { font-weight: 700; }
  @media print { .no-print { display: none; } body { margin: 0; } }
</style>
</head>
<body>
  <p class="no-print"><button onclick="window.print()">Друк</button></p>
  <h1>Everyday Health Index — анкета${snapshot ? ' (приклад із поточних даних)' : ' для заповнення'}</h1>
  <p class="hint">${
    snapshot
      ? 'Заповнена поточними значеннями з форми калькулятора (жирним) — приклад для звірки/демонстрації.'
      : 'Заповніть вручну, потім перенесіть значення у форму калькулятора (вкладка "Вхідні дані").'
  }</p>

  <h2>1. Стартовий Індекс</h2>
  <p class="hint">Оцінка стану за шкалою ${config.ratingScale.min}-${config.ratingScale.max}. Заповнюється при онбордингу, до перших даних дня.</p>
  ${ratingRows}
  ${row('Завершені дні після онбордингу (через кому)', '', snapshot?.baseline.completedResources.join(', '))}

  <h2>2. Ресурс дня</h2>
  ${row('Вода — факт', 'мл', snapshot?.daily.water.fact)}
  ${row('Вода — ціль', 'мл', snapshot?.daily.water.target)}
  ${row('Харчування — факт', 'ккал', snapshot?.daily.nutrition.fact)}
  ${row('Харчування — ціль', 'ккал', snapshot?.daily.nutrition.target)}
  ${row('Активність — факт', 'ккал', snapshot?.daily.activity.fact)}
  ${row('Активність — ціль', 'ккал', snapshot?.daily.activity.target)}
  ${row('Задачі (лише пріоритетні, 1-3) — виконано', '', snapshot?.daily.tasks.completed)}
  ${row('Задачі (лише пріоритетні, 1-3) — заплановано', '', snapshot?.daily.tasks.planned)}
  ${checkboxRow('Графік препаратів активний', snapshot?.daily.medicationActive)}
  ${row('Препарати — прийнято доз', '', snapshot?.daily.medication.completed)}
  ${row('Препарати — заплановано доз', '', snapshot?.daily.medication.planned)}
  ${row('Стан — самооцінка', '1-5', snapshot?.daily.stateRatingEntered ? snapshot.daily.stateRating : undefined)}
  ${checkboxRow('Ціль сну активна', snapshot?.daily.sleepActive)}
  ${row('Сон — факт', 'год', snapshot?.daily.sleep.factHours)}
  ${row('Сон — ціль', 'год', snapshot?.daily.sleep.targetHours)}

  <h2>3. Індекс за 30 днів</h2>
  <p class="hint">Ресурс дня (0-100) за кожен із останніх ${config.rollingWindowDays} завершених днів.</p>
  ${dayRows}
</body>
</html>`;
}

function openQuestionnaireDocument(config: HealthIndexConfig, snapshot?: QuestionnaireSnapshot) {
  const html = buildQuestionnaireHtml(config, snapshot);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

async function copyToClipboard(value: object) {
  try {
    await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
    toastService.success('Дані скопійовано в буфер обміну.');
  } catch {
    toastService.error('Не вдалося скопіювати — буфер обміну недоступний у цьому контексті.');
  }
}

// ---- Модалка з результатом (спільна для всіх 3 калькуляторів) ----

const ResultModal: React.FC<{ state: ResultState; onClose: () => void }> = ({ state, onClose }) => {
  if (!state) return null;
  const { calculator, response } = state;
  const r = response.result;

  return (
    <BaseDialog
      open={!!state}
      onClose={onClose}
      title={`Результат: ${CALCULATOR_TITLES[calculator]}`}
      iDoc={ModalIndices.ADMIN_HEALTH_INDEX_RESULT}
      maxWidth="md"
      actions={
        <Button startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(response)}>
          Копіювати дані
        </Button>
      }
    >
      {calculator === 'daily-resource' && (
        <Box>
          <Typography variant="h6">Ресурс дня: {r.resource}</Typography>

          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {BLOCKS.map((b) => (
                    <TableCell key={b}>{BLOCK_LABELS[b]}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {BLOCKS.map((b) => (
                    <TableCell key={b}>{r.blocks[b] === null ? 'N/A' : r.blocks[b]}</TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>6 напрямків</Typography>
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {DIRECTIONS.map((d) => (
                    <TableCell key={d}>{DIRECTION_LABELS[d]}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {DIRECTIONS.map((d) => (
                    <TableCell key={d}>{r.directions[d]}</TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {r.invariantCheck.applicable ? (
            <Alert severity={r.invariantCheck.withinTolerance ? 'success' : 'warning'} sx={{ mt: 2 }}>
              середнє(6 напрямків) = {r.invariantCheck.directionsAverage}, різниця з Ресурсом дня: {r.invariantCheck.diff}
              {!r.invariantCheck.withinTolerance && ' — конфігурація в "Конфігураторі" розбалансована (сума ваг ≠ 1,20).'}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              T=N/A — інваріант не перевіряється (v1.3: напрямки перенормалізовані без T, не зобов'язані == R_d).
            </Alert>
          )}
        </Box>
      )}

      {calculator === 'rolling-index' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">EHI = {r.value}</Typography>
            <Chip label={r.levelLabel} color={LEVEL_COLOR[r.level] ?? 'default'} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {r.isFullWindow ? `Повне ${r.windowSize}-денне вікно.` : `Часткове вікно — використано ${r.daysUsed} із ${r.windowSize} днів.`}
          </Typography>
        </Box>
      )}

      {calculator === 'baseline-index' && (
        <Box>
          <Typography variant="h6">EHI_start = {r.startIndex}</Typography>
          <Typography variant="h6">EHI_n = {r.blendedIndex}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Завершених днів: {r.daysCompleted}. Підпис: «{r.label}»
          </Typography>
        </Box>
      )}

      <Typography variant="body2" sx={{ mt: 2, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
        {response.explanation}
      </Typography>
    </BaseDialog>
  );
};

// Іменовані сценарії "клієнтів" (2026-08-21, за проханням користувача) — мок-дані онбордингу
// (6 оцінок стану + особисті цілі дня), використовуються і в пресетах DailyResourceGroup/
// BaselineIndexGroup (пілюлі "Клієнт А/Б/С"), і на вкладці "Симуляція" (лише показ, без дій).
// Назва поля НЕ "focus" (2026-08-20, за проханням користувача "оперувати конкретними
// змінними") — task.md сам називає цей вибір "Focus" (PHASE 1.1: "зберегти перший Focus"), але
// саме слово "focus" уже зайняте в системі двічі: значення типу `Direction`/`FocusOption`
// 'focus' (сам напрямок "концентрація") і зовсім інша сутність `DailyFocus`/"Фокус дня" зі спеки
// EHI (spec-condensed-v1.3.md розділ 6 — обчислювана щоденна рекомендація дії, 7-денне вікно).
// `scenario.onboardingFocus === 'focus'` (клієнт обрав "підвищити концентрацію") був би цілком
// реальним і нечитабельним кодом. `onboardingFocus` прив'язує назву до конкретного кроку
// (PHASE 1.1), не конфліктує з жодним з двох інших значень.
type ClientScenario = {
  key: string;
  label: string;
  onboardingFocus: FocusOption;
  profile: ClientProfile;
  data: OnboardingSnapshot;
  phase3: Phase3Data;
  phase4: Phase4Data;
};

// Мок-дані клієнтів-сценаріїв (2026-08-20, за проханням користувача — "засунь їх в один json,
// щоби можна було легко коригувати стартові параметри") — винесено з .tsx у власний JSON-файл
// (`health-index-client-scenarios.json`, імпорт вгорі файлу) — коригувати портрети клієнтів там,
// не тут. Клієнти D/E — додаткові нестандартні зразки (за проханням користувача, "просто якісь
// вигадані зразки нестандартних ситуацій"): D — стать не вказана (перевірка костиля
// effectiveGender→female, technical-debt.md п. 13), E — похилий вік (68 років, гілка "elderly"
// множника віку в WDNA/BMR) + goal 'endurance' (раніше не покритий жодним клієнтом — гілка AMDR
// 'carb_heavy').
const CLIENT_SCENARIOS = clientScenariosData as ClientScenario[];

// ---- Група 1: Ресурс дня (daily-resource) ----

type DailyResourcePreset = {
  label: string;
  water: { fact: number; target: number };
  nutrition: { fact: number; target: number };
  activity: { fact: number; target: number };
  tasks: { completed: number; planned: number };
  medicationActive: boolean;
  medication: { completed: number; planned: number };
  stateRatingEntered: boolean;
  stateRating: number;
  sleepActive: boolean;
  sleep: { factHours: number; targetHours: number };
};

// Швидке заповнення для перевірки (2026-08-21, за проханням користувача) — ті самі сценарії, що
// й у health-index.formulas.spec.ts (усі 0 / усі 100 / T=N/A), плюс реалістичний день з
// health-index.service.ts EXAMPLE_PARAMS.
const DAILY_RESOURCE_PRESETS: DailyResourcePreset[] = [
  {
    label: 'Усі входи 0',
    water: { fact: 0, target: 2500 },
    nutrition: { fact: 0, target: 2000 },
    activity: { fact: 0, target: 400 },
    tasks: { completed: 0, planned: 3 },
    medicationActive: true,
    medication: { completed: 0, planned: 2 },
    stateRatingEntered: true,
    stateRating: 1, // U=(1-1)*25=0
    sleepActive: true,
    sleep: { factHours: 0, targetHours: 8 },
  },
  {
    label: 'Усі входи 100 (максимум)',
    water: { fact: 2500, target: 2500 },
    nutrition: { fact: 2000, target: 2000 },
    activity: { fact: 400, target: 400 },
    tasks: { completed: 3, planned: 3 },
    medicationActive: true,
    medication: { completed: 2, planned: 2 },
    stateRatingEntered: true,
    stateRating: 5, // U=(5-1)*25=100
    sleepActive: true,
    sleep: { factHours: 8, targetHours: 8 },
  },
  {
    label: 'T = N/A (немає пріоритетних задач і доз)',
    water: { fact: 2000, target: 2500 },
    nutrition: { fact: 1800, target: 2000 },
    activity: { fact: 300, target: 400 },
    tasks: { completed: 0, planned: 0 },
    medicationActive: false,
    medication: { completed: 0, planned: 0 },
    stateRatingEntered: true,
    stateRating: 3,
    sleepActive: true,
    sleep: { factHours: 7, targetHours: 8 },
  },
  {
    label: 'Реалістичний день (EXAMPLE_PARAMS)',
    water: { fact: 2000, target: 2500 },
    nutrition: { fact: 1800, target: 2000 },
    activity: { fact: 350, target: 400 },
    tasks: { completed: 2, planned: 3 },
    medicationActive: true,
    medication: { completed: 1, planned: 2 },
    stateRatingEntered: true,
    stateRating: 4,
    sleepActive: true,
    sleep: { factHours: 7, targetHours: 8 },
  },
  // Клієнт А/Б/С (2026-08-21, за проханням користувача) — цілі щойно з онбордингу
  // (CLIENT_SCENARIOS), факт = 0 і оцінку ще не внесено — день 1, нічого ще не зроблено.
  ...CLIENT_SCENARIOS.map((scenario) => ({
    label: scenario.label,
    water: { fact: 0, target: scenario.data.water },
    nutrition: { fact: 0, target: scenario.data.nutrition },
    activity: { fact: 0, target: scenario.data.activity },
    tasks: { completed: 0, planned: scenario.data.tasksPlanned },
    medicationActive: scenario.data.medicationActive,
    medication: { completed: 0, planned: scenario.data.medicationPlanned },
    stateRatingEntered: false,
    stateRating: 3,
    sleepActive: scenario.data.sleepActive,
    sleep: { factHours: 0, targetHours: scenario.data.sleepTargetHours },
  })),
];

const DailyResourceGroup = React.forwardRef<DailyResourceGroupHandle, { config: HealthIndexConfig; onResult: (r: CalculateResponse) => void }>(({
  config,
  onResult,
}, ref) => {
  const [water, setWater] = useState({ fact: 2000, target: 2500 });
  const [nutrition, setNutrition] = useState({ fact: 1800, target: 2000 });
  const [activity, setActivity] = useState({ fact: 350, target: 400 });
  // Лише пріоритетні задачі ("Для себе/для мети", максимум 1-3 на день) — v1.3.
  const [tasks, setTasks] = useState({ completed: 2, planned: 3 });
  const [medicationActive, setMedicationActive] = useState(true);
  const [medication, setMedication] = useState({ completed: 1, planned: 2 });
  // Пряма самооцінка стану 1-5 (замінює колишній feelings.savedEntries/targetEntries) — v1.3.
  const [stateRatingEntered, setStateRatingEntered] = useState(true);
  const [stateRating, setStateRating] = useState(4);
  const [sleepActive, setSleepActive] = useState(true);
  const [sleep, setSleep] = useState({ factHours: 7, targetHours: 8 });
  const [loading, setLoading] = useState(false);
  const [presetIndex, setPresetIndex] = useState('');

  useImperativeHandle(ref, () => ({
    getSnapshot: () => ({
      water,
      nutrition,
      activity,
      tasks,
      medicationActive,
      medication,
      stateRatingEntered,
      stateRating,
      sleepActive,
      sleep,
    }),
  }));

  const applyPreset = (index: string) => {
    setPresetIndex(index);
    if (index === '') return;
    const preset = DAILY_RESOURCE_PRESETS[Number(index)];
    setWater(preset.water);
    setNutrition(preset.nutrition);
    setActivity(preset.activity);
    setTasks(preset.tasks);
    setMedicationActive(preset.medicationActive);
    setMedication(preset.medication);
    setStateRatingEntered(preset.stateRatingEntered);
    setStateRating(preset.stateRating);
    setSleepActive(preset.sleepActive);
    setSleep(preset.sleep);
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const response = await calculateHealthIndex(
        'daily-resource',
        {
          water,
          nutrition,
          activity,
          tasks,
          medication: { ...medication, active: medicationActive },
          stateRating: stateRatingEntered ? stateRating : null,
          sleep: { ...sleep, active: sleepActive },
        },
        config,
      );
      onResult(response);
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося розрахувати Ресурс дня.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>2. Ресурс дня</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        R_d = середнє 5 блоків (A, F, T, N, W), або 4 (без T, якщо N/A) + 6 похідних напрямків.{' '}
        <SourceRefs orig="4-5" spec="2-3" />
      </Typography>

      <Typography variant="body2" sx={{ mb: 0.5 }}>Приклад (швидке заповнення):</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {DAILY_RESOURCE_PRESETS.map((preset, i) => (
          <Button
            key={preset.label}
            size="small"
            variant={presetIndex === String(i) ? 'contained' : 'outlined'}
            onClick={() => applyPreset(String(i))}
          >
            {preset.label}
          </Button>
        ))}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <ParamLabel label="Вода (мл)" param="W ← fact_water, target_water" required color="info" quoteKey="W" />
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField label="Факт (fact_water)" type="number" value={water.fact} onChange={(e) => setWater({ ...water, fact: Number(e.target.value) })} />
            <TextField label="Ціль (target_water)" type="number" value={water.target} onChange={(e) => setWater({ ...water, target: Number(e.target.value) })} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ParamLabel label="Харчування (ккал)" param="N ← fact_kcal, target_kcal" required color="success" quoteKey="N" />
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField label="Факт (fact_kcal)" type="number" value={nutrition.fact} onChange={(e) => setNutrition({ ...nutrition, fact: Number(e.target.value) })} />
            <TextField label="Ціль (target_kcal)" type="number" value={nutrition.target} onChange={(e) => setNutrition({ ...nutrition, target: Number(e.target.value) })} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ParamLabel label="Активність (ккал)" param="A ← fact_active_kcal, target_active_kcal" required color="warning" quoteKey="A" />
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField label="Факт (fact_active_kcal)" type="number" value={activity.fact} onChange={(e) => setActivity({ ...activity, fact: Number(e.target.value) })} />
            <TextField label="Ціль (target_active_kcal)" type="number" value={activity.target} onChange={(e) => setActivity({ ...activity, target: Number(e.target.value) })} />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <ParamLabel label="Задачі (лише пріоритетні, 1-3 на день)" param="Q ← completed_priority_tasks, planned_priority_tasks" required color="secondary" quoteKey="Q" />
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField label="Виконано (completed_priority_tasks)" type="number" value={tasks.completed} onChange={(e) => setTasks({ ...tasks, completed: Number(e.target.value) })} />
            <TextField label="Заплановано (planned_priority_tasks)" type="number" value={tasks.planned} onChange={(e) => setTasks({ ...tasks, planned: Number(e.target.value) })} />
          </Box>
          <Typography variant="caption" color="text.secondary">planned_priority_tasks=0 → Q=N/A (не 0)</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ParamLabel label="Препарати" param="M ← completed_doses, planned_doses" color="secondary" quoteKey="M" />
          <FormControlLabel
            control={<Switch size="small" checked={medicationActive} onChange={(e) => setMedicationActive(e.target.checked)} />}
            label="графік активний (medication.active)"
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField
              label="Прийнято (completed_doses)"
              type="number"
              disabled={!medicationActive}
              value={medication.completed}
              onChange={(e) => setMedication({ ...medication, completed: Number(e.target.value) })}
            />
            <TextField
              label="Заплановано (planned_doses)"
              type="number"
              disabled={!medicationActive}
              value={medication.planned}
              onChange={(e) => setMedication({ ...medication, planned: Number(e.target.value) })}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ParamLabel label="Стан (самооцінка)" param="U ← state_rating" required color="primary" quoteKey="U" />
          <FormControlLabel
            control={<Switch size="small" checked={stateRatingEntered} onChange={(e) => setStateRatingEntered(e.target.checked)} />}
            label="оцінку внесено (state_rating ≠ null)"
          />
          <TextField
            label="Оцінка, 1-5 (state_rating)"
            type="number"
            inputProps={{ min: 1, max: 5 }}
            disabled={!stateRatingEntered}
            value={stateRating}
            onChange={(e) => setStateRating(Number(e.target.value))}
            sx={{ mt: 0.5, display: 'block' }}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Оцінку не внесено → state_rating=null → U=0 (не N/A)
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <ParamLabel label="Сон" param="S ← fact_sleep_hours, target_sleep_hours" color="primary" quoteKey="S" />
          <FormControlLabel
            control={<Switch size="small" checked={sleepActive} onChange={(e) => setSleepActive(e.target.checked)} />}
            label="ціль активна (sleep.active)"
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField
              label="Факт, год (fact_sleep_hours)"
              type="number"
              disabled={!sleepActive}
              value={sleep.factHours}
              onChange={(e) => setSleep({ ...sleep, factHours: Number(e.target.value) })}
            />
            <TextField
              label="Ціль, год (target_sleep_hours)"
              type="number"
              disabled={!sleepActive}
              value={sleep.targetHours}
              onChange={(e) => setSleep({ ...sleep, targetHours: Number(e.target.value) })}
            />
          </Box>
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        <Box component="span" sx={{ color: 'error.main' }}>*</Box> — обовʼязкове поле (`requiredParams` з GET :name/info). Технічна назва в дужках/бейджі — точний ключ у JSON-тілі запиту.
      </Typography>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleCalculate} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Розрахувати'}
        </Button>
      </Box>
    </Box>
  );
});
DailyResourceGroup.displayName = 'DailyResourceGroup';

// ---- Група 2: Індекс за 30 днів (rolling-index) ----

// Ті самі сценарії, що й у health-index.formulas.spec.ts.
const ROLLING_INDEX_PRESETS: { label: string; value: string }[] = [
  { label: 'Часткове вікно (5 днів)', value: '77, 80, 65, 90, 73' },
  { label: '30 однакових днів по 80 (повне вікно)', value: new Array(30).fill(80).join(', ') },
  { label: 'Зростаючий тренд (10 днів)', value: '40, 45, 50, 55, 60, 65, 70, 75, 80, 85' },
];

const RollingIndexGroup = React.forwardRef<RollingIndexGroupHandle, { config: HealthIndexConfig; onResult: (r: CalculateResponse) => void }>(({
  config,
  onResult,
}, ref) => {
  const [raw, setRaw] = useState('77, 80, 65, 90, 73');
  const [presetIndex, setPresetIndex] = useState('');
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    getSnapshot: () => ({ dailyResources: parseNumberList(raw).filter((n) => !Number.isNaN(n)) }),
  }));

  const applyPreset = (index: string) => {
    setPresetIndex(index);
    if (index === '') return;
    setRaw(ROLLING_INDEX_PRESETS[Number(index)].value);
  };

  const handleCalculate = async () => {
    const dailyResources = parseNumberList(raw);
    if (dailyResources.some((n) => Number.isNaN(n))) {
      toastService.error('Список днів має містити лише числа, розділені комою.');
      return;
    }
    setLoading(true);
    try {
      const response = await calculateHealthIndex('rolling-index', { dailyResources }, config);
      onResult(response);
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося розрахувати Індекс.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>3. Індекс за 30 днів</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        EHI = середнє масиву завершених денних Ресурсів (до {config.rollingWindowDays} останніх). <SourceRefs orig="6" spec="4" /><br />
        Формула щоденного оновлення (ковзне вікно) <SpecQuote quoteKey="rollingUpdate" />
      </Typography>

      <Typography variant="body2" sx={{ mb: 0.5 }}>Приклад (швидке заповнення):</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {ROLLING_INDEX_PRESETS.map((preset, i) => (
          <Button
            key={preset.label}
            size="small"
            variant={presetIndex === String(i) ? 'contained' : 'outlined'}
            onClick={() => applyPreset(String(i))}
          >
            {preset.label}
          </Button>
        ))}
      </Box>

      <ParamLabel label="Ресурси днів (через кому, найновіший останнім)" param="dailyResources" required quoteKey="dailyResources" />
      <TextField
        fullWidth
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
      />

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleCalculate} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Розрахувати'}
        </Button>
      </Box>
    </Box>
  );
});
RollingIndexGroup.displayName = 'RollingIndexGroup';

// ---- Група 3: Стартовий Індекс (baseline-index) ----

type BaselinePreset = { label: string; selfAssessment: Record<DirectionKey, number>; completedRaw: string };

const ALL_THREES: Record<DirectionKey, number> = { physical: 3, energy: 3, clarity: 3, productivity: 3, focus: 3, emotional: 3 };

// Ті самі сценарії, що й у health-index.formulas.spec.ts (статуси start/forming/preliminary/full).
const BASELINE_INDEX_PRESETS: BaselinePreset[] = [
  { label: 'Лише онбординг (статус "start")', selfAssessment: ALL_THREES, completedRaw: '' },
  { label: 'Перші 5 днів ("forming")', selfAssessment: { physical: 3, energy: 4, clarity: 2, productivity: 3, focus: 4, emotional: 3 }, completedRaw: '70, 75, 68, 80, 72' },
  { label: '7 днів ("preliminary")', selfAssessment: ALL_THREES, completedRaw: new Array(7).fill(70).join(', ') },
  { label: '30 днів ("full")', selfAssessment: ALL_THREES, completedRaw: new Array(30).fill(70).join(', ') },
  // Клієнт А/Б/С (2026-08-21, за проханням користувача) — ті самі 6 оцінок, що й у CLIENT_SCENARIOS,
  // completedRaw порожній (день 0, щойно онбординг).
  ...CLIENT_SCENARIOS.map((scenario) => ({ label: scenario.label, selfAssessment: scenario.data.selfAssessment, completedRaw: '' })),
];

const BaselineIndexGroup = React.forwardRef<BaselineIndexGroupHandle, { config: HealthIndexConfig; onResult: (r: CalculateResponse) => void }>(({
  config,
  onResult,
}, ref) => {
  const [selfAssessment, setSelfAssessment] = useState<Record<DirectionKey, number>>(ALL_THREES);
  const [completedRaw, setCompletedRaw] = useState('');
  const [presetIndex, setPresetIndex] = useState('');
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    getSnapshot: () => ({ selfAssessment, completedResources: parseNumberList(completedRaw).filter((n) => !Number.isNaN(n)) }),
  }));

  const applyPreset = (index: string) => {
    setPresetIndex(index);
    if (index === '') return;
    const preset = BASELINE_INDEX_PRESETS[Number(index)];
    setSelfAssessment(preset.selfAssessment);
    setCompletedRaw(preset.completedRaw);
  };

  const handleCalculate = async () => {
    const completedResources = completedRaw.trim() ? parseNumberList(completedRaw) : [];
    if (completedResources.some((n) => Number.isNaN(n))) {
      toastService.error('Список завершених днів має містити лише числа, розділені комою.');
      return;
    }
    setLoading(true);
    try {
      const response = await calculateHealthIndex('baseline-index', { selfAssessment, completedResources }, config);
      onResult(response);
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося розрахувати Стартовий Індекс.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>1. Стартовий Індекс</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        EHI_start із {DIRECTIONS.length} оцінок стану ({config.ratingScale.min}-{config.ratingScale.max}) + плавна заміна реальними
        днями. <SourceRefs orig="7" spec="5" />
      </Typography>

      <Typography variant="body2" sx={{ mb: 0.5 }}>Приклад (швидке заповнення):</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {BASELINE_INDEX_PRESETS.map((preset, i) => (
          <Button
            key={preset.label}
            size="small"
            variant={presetIndex === String(i) ? 'contained' : 'outlined'}
            onClick={() => applyPreset(String(i))}
          >
            {preset.label}
          </Button>
        ))}
      </Box>

      <ParamLabel label="Оцінки 6 напрямків" param="selfAssessment" required quoteKey="selfAssessment" />
      <Grid container spacing={2}>
        {DIRECTIONS.map((d) => (
          <Grid item xs={6} sm={4} md={2} key={d}>
            <TextField
              label={`${DIRECTION_LABELS[d]} (${d})`}
              type="number"
              inputProps={{ min: config.ratingScale.min, max: config.ratingScale.max }}
              value={selfAssessment[d]}
              onChange={(e) => setSelfAssessment({ ...selfAssessment, [d]: Number(e.target.value) })}
            />
          </Grid>
        ))}
      </Grid>

      <ParamLabel label="Завершені дні після онбордингу" param="completedResources" quoteKey="completedResources" />
      <TextField
        label="Опційно, через кому"
        fullWidth
        value={completedRaw}
        onChange={(e) => setCompletedRaw(e.target.value)}
        placeholder="70, 75, 68, 80, 72"
      />

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleCalculate} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Розрахувати'}
        </Button>
      </Box>
    </Box>
  );
});
BaselineIndexGroup.displayName = 'BaselineIndexGroup';

// ---- Таб "Симуляція" — режисеруємо сценарій "як зібрати перші дані" (2026-08-21) ----
// Профіль (кроки 2-9 онбордингу) — редаговані інпути (2026-08-21, за проханням користувача):
// це саме ті поля, які реально годують calculated-metrics (BMR/AMR/WDNA), тому мають бути
// живими для експериментів, на відміну від "6 оцінок стану"/"цілі дня" нижче (свідомо read-only
// мок). Розрахунок і перенесення в поля Inputs-табу роблять пілюлі "Клієнт А/Б/С", вбудовані в
// наявні "Приклад" ряди DailyResourceGroup/BaselineIndexGroup — не тут.
const ProfileCard: React.FC<{
  label: string;
  profile: ClientProfile;
  onChange: (next: ClientProfile) => void;
}> = ({ label, profile, onChange }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{label}</Typography>

    {/* Проміжний value-message екран (2026-08-20, PHASE 2, task.md рядок 131-133) — Figma-дизайну
        поки немає (аналогічно PHASE 1), тому схематичний плейсхолдер, той самий компонент, що й
        для PHASE 1. Умовна i-doc назва за конвенцією проєкту (page:onboarding:...) — ще не
        підтверджена реальним Figma-шаром, як phase0/phase1. */}
    <Box sx={{ textAlign: 'center', mb: 1.5 }}>
      <ValueMessageWireframe title="Сформуємо план під вашу ціль" body="Спочатку оберіть, чого хочете досягти. Після цього додайте кілька базових даних — вони допоможуть Everyday розрахувати персональні цілі щодо харчування, води та активності." />
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <PageNameTag name="page:onboarding:goal:intro" />
        {/* Тимчасово закоментовано (2026-08-26, за проханням користувача). <SpecQuote quoteKey="phase2ValueMessage" /> */}
      </Box>
    </Box>

    <Grid container spacing={1.5}>
      {/* Порядок полів — буквально за задачею продакту (Phase 2, "ВАЖЛИВО: основна ціль повинна
          бути першою, навіть якщо у Figma зараз вона розташована після антропометричних питань"),
          НЕ порядок оголошення типу ClientProfile. */}
      {/* Реальні назви шарів Figma (2026-08-20, node-id 2003-6964..7683 діапазон, підтверджено
          get_metadata/копіпастом CSS від користувача) — картинки ще не завантажені (ліміт Figma
          MCP), але назви вже точні, не вигадані. */}
      <Grid item xs={12}>
        {/* Без ParamLabel/Chip технічної назви (2026-08-20, за проханням користувача — "із
            кепшенів прибери всі назви полів, бо вони є в таблицях") — технічна назва вже видна
            в стовпчику "Поле" таблиці нижче, дублювати в підписі не треба. */}
        {/* Реальний скрін (2026-08-20, PNG наданий користувачем напряму в чат, не через Figma MCP
            — rate-limit ще діяв). Заголовок екрана "Вхідне опитування" зі step-progress крапками
            — спільний для всіх 6 питань фази, не повторюємо в кожному підписі. */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box component="img" src="/onboarding/phase2-main-goal.png" alt="page:onboarding:main-goal" sx={{ width: 90, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Основна ціль</Typography>
        <PageNameTag name="page:onboarding:main-goal" sx={{ mt: 0.5 }} />
        {/* НЕ те саме, що "Focus" з PHASE 1.1 (onboardingFocus) — там пріоритет серед 6
            напрямків EHI (концентрація/енергія/...), тут — ціль трансформації тіла (майбутнє
            призначення: обрати гілку maintenance/weightLoss/weightGain з виводу AMR-калькулятора,
            `calculated-metrics/calculators/amr.calculator.ts`). Дві різні сутності продукту,
            2026-08-20, за питанням користувача "це мабуть вже друга ціль". Обидва поля — і
            `goal`, і `onboardingFocus` — на РОЗРАХУНОК зараз НЕ впливають: EHI використовує лише
            selfAssessment (PHASE 1), а Phase2ResultCard нижче показує статичні числа зі
            сценарію, не перераховує їх з цього поля — підтверджено користувачем, "обидві цілі на
            калькуляцію не впливають". */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Не плутати з "Focus" (PHASE 1.1) — ціль трансформації тіла. На поточний розрахунок не впливає, лише зберігається.
        </Typography>
        {/* Таблиця замість select (2026-08-20, за проханням користувача — "виведи таблицею з
            усіма значеннями так, як зробив з фокусом"), той самий формат, що й PHASE 1.1
            (Варіант/Поле/Значення/Обрано). На відміну від Focus (лише показ), тут поле РЕДАГОВАНЕ
            — рядок клікабельний і викликає onChange, як раніше робив select. */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Варіант</TableCell>
                <TableCell>Поле</TableCell>
                <TableCell>Значення</TableCell>
                <TableCell align="center">Обрано</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(Object.keys(GOAL_LABELS) as Goal[]).map((goal) => {
                const isSelected = goal === profile.goal;
                return (
                  <TableRow
                    key={goal}
                    selected={isSelected}
                    hover
                    onClick={() => onChange({ ...profile, goal })}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{GOAL_LABELS[goal]}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.main</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{`'${goal}'`}</TableCell>
                    <TableCell align="center">
                      {isSelected && <CheckCircleIcon fontSize="small" color="primary" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box component="img" src="/onboarding/phase2-sex.png" alt="page:onboarding:sex" sx={{ width: 90, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Стать</Typography>
        <PageNameTag name="page:onboarding:sex" sx={{ mt: 0.5 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Варіант</TableCell>
                <TableCell>Поле</TableCell>
                <TableCell>Значення</TableCell>
                <TableCell align="center">Обрано</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(Object.keys(GENDER_LABELS) as Gender[]).map((gender) => {
                const isSelected = gender === profile.gender;
                return (
                  <TableRow
                    key={gender}
                    selected={isSelected}
                    hover
                    onClick={() => onChange({ ...profile, gender })}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{GENDER_LABELS[gender]}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>gender</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{`'${gender}'`}</TableCell>
                    <TableCell align="center">
                      {isSelected && <CheckCircleIcon fontSize="small" color="primary" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      {/* Числові поля (вік/зріст/вага) — однотипно з рештою полів фази (2026-08-20, за проханням
          користувача "зроби все однотипно в цій фазі"): ParamLabel(label, param) + підпис
          page:onboarding:... НАД таблицею, як у Основна ціль/Стать/Рівень активності — а не
          всередині клітинки таблиці ("винеси з таблиці до заголовка"). Таблиця — лише "Значення"
          (без інпута), Поле/назва вже назвались вище, повторювати в таблиці нема сенсу. */}
      <Grid item xs={12}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box component="img" src="/onboarding/phase2-age.png" alt="page:onboarding:age" sx={{ width: 90, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Вік</Typography>
        <PageNameTag name="page:onboarding:age" sx={{ mt: 0.5 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Поле</TableCell>
                <TableCell>Значення</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>age</TableCell>
                <TableCell>
                  <Chip label={profile.age} color="primary" size="small" sx={{ fontWeight: 700 }} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box component="img" src="/onboarding/phase2-height.png" alt="page:onboarding:height" sx={{ width: 90, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Зріст (см)</Typography>
        <PageNameTag name="page:onboarding:height" sx={{ mt: 0.5 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Поле</TableCell>
                <TableCell>Значення</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>height</TableCell>
                <TableCell>
                  <Chip label={profile.height} color="primary" size="small" sx={{ fontWeight: 700 }} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box component="img" src="/onboarding/phase2-weight.png" alt="page:onboarding:weight" sx={{ width: 90, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Вага (кг)</Typography>
        <PageNameTag name="page:onboarding:weight" sx={{ mt: 0.5 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Поле</TableCell>
                <TableCell>Значення</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>weight</TableCell>
                <TableCell>
                  <Chip label={profile.weight} color="primary" size="small" sx={{ fontWeight: 700 }} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box component="img" src="/onboarding/phase2-activity-level.png" alt="page:onboarding:activity-level" sx={{ width: 90, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Рівень активності</Typography>
        <PageNameTag name="page:onboarding:activity-level" sx={{ mt: 0.5 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Варіант</TableCell>
                <TableCell>Поле</TableCell>
                <TableCell>Значення</TableCell>
                <TableCell align="center">Обрано</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ONBOARDING_ACTIVITY_LEVELS.map((level) => {
                const isSelected = level === profile.activityLevel;
                return (
                  <TableRow
                    key={level}
                    selected={isSelected}
                    hover
                    onClick={() => onChange({ ...profile, activityLevel: level })}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{ACTIVITY_LEVEL_LABELS[level]}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>activityLevel</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{`'${level}'`}</TableCell>
                    <TableCell align="center">
                      {isSelected && <CheckCircleIcon fontSize="small" color="primary" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  </Box>
);

// Виключно дані, без жодних дій (за проханням користувача — "не плутай дії, всі дії в сторінці
// вхідних даних"): ця вкладка лише зберігає й показує мок-набори одночасно. Розрахунок і
// перенесення в поля роблять пілюлі "Клієнт А/Б/С", вбудовані в наявні "Приклад" ряди
// DailyResourceGroup/BaselineIndexGroup (CLIENT_SCENARIOS оголошено раніше у файлі — обидва
// ряди пресетів його використовують).
// Ключі SPEC_QUOTES для 6 дослівних тверджень самооцінки (PHASE 1, task.md рядки 73-83) — за
// напрямком, у тому ж порядку, що й DIRECTIONS.
const PHASE1_QUOTE_KEYS: Record<string, string> = {
  physical: 'q_physical',
  energy: 'q_energy',
  clarity: 'q_clarity',
  productivity: 'q_productivity',
  focus: 'q_focus',
  emotional: 'q_emotional',
};

// Реальні скріни з Figma (2026-08-21, копія користувача 25OAhYLPVIU31wXWB4kgWo) — усі 6 нод
// названі однаково `page:onboarding:health-assessment` (спільний компонент-шаблон, текст
// підставляється), різняться лише за node-id: 2009-14563/14667/14745/14907/14986/15062.
const PHASE1_QUESTION_IMAGES: Record<string, string> = {
  physical: '/onboarding/phase1-q1-physical.png',
  energy: '/onboarding/phase1-q2-energy.png',
  clarity: '/onboarding/phase1-q3-clarity.png',
  productivity: '/onboarding/phase1-q4-productivity.png',
  focus: '/onboarding/phase1-q5-focus.png',
  emotional: '/onboarding/phase1-q6-emotional.png',
};

// Екран "value message" перед питаннями (task.md, «Дізнайтесь свою стартову точку») дизайну
// не має — користувач попросив зробити схематичний скрін і вигадати назву (2026-08-21). CSS-
// wireframe, не картинка, з ЯВНОЮ позначкою "дизайну ще немає" — щоб ніхто не сплутав із
// реальним скріном поруч.
// Пропорції такі самі, як у реальних скрінів поруч (375x812 при ширині 90 ≈ висота 195) —
// aspect-ratio, не фіксована висота з магічними відступами (це і "розтягнуло все" минулого разу).
// Не малюємо ілюстрацію (2026-08-21, за проханням користувача — "ми його не будемо малювати") —
// дослівний текст value message з task.md, дрібним шрифтом, без жодного дизайну.
// Параметризовано (2026-08-20, PHASE 2) — той самий схематичний плейсхолдер тепер годиться для
// будь-якого проміжного value-message екрана без Figma-дизайну, не лише PHASE 1.
const ValueMessageWireframe: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <Box sx={{ border: '1px dashed', borderColor: 'text.disabled', borderRadius: 1, width: 90, aspectRatio: '375 / 812', p: 1, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
    <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, lineHeight: 1.2 }}>
      «{title}»
    </Typography>
    <Typography sx={{ fontSize: '0.45rem', lineHeight: 1.2, mt: 0.5, color: 'text.secondary' }}>
      {body}
    </Typography>
    <Box sx={{ flexGrow: 1 }} />
    <Box sx={{ height: 20, bgcolor: 'text.disabled', opacity: 0.3, borderRadius: 0.5 }} />
  </Box>
);

// Імітація 5-бальної шкали оцінки з екрана онбордингу (config.ratingScale, за замовчуванням 1-5,
// 2026-08-20, за проханням користувача — "зроби імітацію шкали 1 2 3 4 5 і виділи значення яке
// обрано"). Симуляція свідомо завжди показує дефолтну шкалу — не приймає `config`, бо не робить
// жодних запитів/розрахунків (лише рав-дані), а конфігуратор впливає лише на розрахунок.
const RATING_SCALE_VALUES = [1, 2, 3, 4, 5];
const RatingScalePreview: React.FC<{ value: number }> = ({ value }) => (
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    {RATING_SCALE_VALUES.map((n) => (
      <Chip
        key={n}
        label={n}
        size="small"
        color={n === value ? 'primary' : 'default'}
        variant={n === value ? 'filled' : 'outlined'}
        sx={{ minWidth: 28, fontWeight: n === value ? 700 : 400 }}
      />
    ))}
  </Box>
);

const Phase1Card: React.FC<{ scenario: (typeof CLIENT_SCENARIOS)[number] }> = ({ scenario }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{scenario.label}</Typography>

    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mb: 2, pb: 0.5 }}>
      <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
        <ValueMessageWireframe
          title="Дізнайтесь свою стартову точку"
          body="Відповідайте на кілька коротких запитань про ваш поточний стан. На основі відповідей Everyday сформує ваш перший Health Index і покаже, які сфери зараз потребують найбільше уваги."
        />
        <PageNameTag name="page:onboarding:health-assessment:intro" sx={{ mt: 0.5 }} />
      </Box>
      {/* Усі 6 питань — одна й та сама сторінка Figma (page:onboarding:health-assessment) з
          різним текстом/обраним рейтингом, тому досить одного прикладу, а не 6 однакових. */}
      <Box sx={{ textAlign: 'center', flexShrink: 0, width: 90 }}>
        <Box component="img" src={PHASE1_QUESTION_IMAGES.physical} alt="page:onboarding:health-assessment" sx={{ width: 90, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        <PageNameTag name="page:onboarding:health-assessment" sx={{ mt: 0.5, px: 0.5, py: 0.1 }} />
      </Box>
    </Box>

    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Напрямок / поле</TableCell>
            <TableCell>Значення</TableCell>
            <TableCell>Твердження (task.md)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {DIRECTIONS.map((d) => (
            <TableRow key={d}>
              <TableCell>
                {DIRECTION_LABELS[d]}
                <Typography display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  selfAssessment.{d}
                </Typography>
              </TableCell>
              <TableCell>
                <RatingScalePreview value={scenario.data.selfAssessment[d]} />
              </TableCell>
              <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{SPEC_QUOTES[PHASE1_QUOTE_KEYS[d]].quote}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const Phase1_1Card: React.FC<{ scenario: (typeof CLIENT_SCENARIOS)[number] }> = ({ scenario }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{scenario.label}</Typography>

    {/* Реальний скрін з Figma (2026-08-20), node-id 2009-15869 → page:onboarding:focus:selected. */}
    <Box sx={{ textAlign: 'center', mb: 1.5 }}>
      <Box component="img" src="/onboarding/phase1_1-focus.png" alt="page:onboarding:focus:selected" sx={{ width: 100, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
      <PageNameTag name="page:onboarding:focus:selected" sx={{ mt: 0.5 }} />
    </Box>

    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Варіант</TableCell>
            <TableCell>Поле</TableCell>
            <TableCell>Значення</TableCell>
            <TableCell align="center">Обрано</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {FOCUS_OPTIONS.map((option) => {
            const isSelected = option === scenario.onboardingFocus;
            return (
              <TableRow key={option} selected={isSelected}>
                <TableCell>{FOCUS_LABELS[option]}</TableCell>
                {/* Поле й значення показані на КОЖНОМУ рядку, не лише на обраному (2026-08-20,
                    за проханням користувача — "виводь в таблицю всі значення", "одразу
                    позначаємо все іменами та значеннями"): кожен варіант — це можливе значення
                    того самого поля, тому кожен рядок підписує і поле, і своє конкретне значення;
                    "Обрано" лишається єдиним індикатором, яке саме застосовано.
                    Назва поля — УЗГОДЖЕНА `goals.focus` (роадмеп, ehi/plan.md п. 6), НЕ поточна
                    технічна назва в моку (`onboardingFocus`, ще не перейменована в коді). */}
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.focus</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{`'${option}'`}</TableCell>
                <TableCell align="center">
                  {isSelected && <CheckCircleIcon fontSize="small" color="primary" />}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// Права колонка "логіка застосунку" (2026-08-21, за проханням користувача — "зліва рав дані,
// справа якась логіка додатку з цими даними"): відрізняється фоном від лівої (рав-дані), лише
// MUI system color, без хардкоду (web/CLAUDE.md).
const LogicPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%', bgcolor: 'action.hover' }}>
    {children}
  </Box>
);

// Стартовий Індекс (PHASE 1, результат) — 2026-08-21, за проханням користувача ("у фазі 1 у нас
// же використовується готовий калькулятор ehi?" → "треба прикрутити реальний — він в нас вже є"):
// справжній виклик `baseline-index` (`calculateHealthIndex`, той самий сервіс, що й вкладка
// "Вхідні дані" на цій сторінці) замість JS-копії формули `(rating-1)*25`. Другий свідомий виняток
// із правила "Симуляція не робить запитів" (перший — DailyNorms у PHASE 2).
const Phase1LogicPanel: React.FC<{ scenario: (typeof CLIENT_SCENARIOS)[number] }> = ({ scenario }) => {
  const [result, setResult] = useState<{ directionScores: number[]; startIndex: number } | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Тимчасово не читається — тултіп із поясненням закоментовано нижче (2026-08-26).
  void explanation;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    calculateHealthIndex('baseline-index', { selfAssessment: scenario.data.selfAssessment })
      .then((res) => {
        if (cancelled) return;
        setResult(res.result);
        setExplanation(res.explanation ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, 'Не вдалося порахувати Стартовий Індекс'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scenario.data.selfAssessment]);

  return (
    <LogicPanel>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flexShrink: 0 }}>
          <Box component="img" src="/onboarding/phase1-result.png" alt="page:onboarding:health-assessment:result" sx={{ width: 90, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
          <PageNameTag name="page:onboarding:health-assessment:result" sx={{ mt: 0.5, px: 0.5, py: 0.1 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            «Вітаємо! Ваш перший Індекс здоров'я готовий» — рахується одразу з 6 оцінок, без профілю.
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Стартовий Індекс: {loading && <CircularProgress size={14} />}
            {!loading && error && <Typography component="span" variant="body2" color="error">{error}</Typography>}
            {!loading && !error && result && (
              <Chip label={result.startIndex.toFixed(1)} color="success" size="small" variant="outlined" sx={{ fontWeight: 700 }} />
            )}
            {/* Розраховано калькулятором `baseline-index` — живе тут-таки, на вкладці "Вхідні
                дані" тієї самої сторінки (не /calculated-metrics, окремий модуль health-index). */}
            <Link component={RouterLink} to="?tab=inputs" sx={{ fontSize: '0.75rem' }}>baseline-index</Link>
            {/* Тимчасово закоментовано (2026-08-26, за проханням користувача) — тултіп-підказка
                прибрана зі сторінки Симуляції, код лишається для відновлення пізніше.
            <Tooltip arrow title={explanation ?? (loading ? 'Рахується…' : 'Недоступно')}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
            */}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {result && DIRECTIONS.map((d, i) => `${DIRECTION_LABELS[d]}: ${result.directionScores[i]}`).join(' · ')}
          </Typography>
        </Box>
      </Box>
    </LogicPanel>
  );
};

const Phase1_1LogicPanel: React.FC = () => (
  <LogicPanel>
    <Typography variant="body2">
      Отримали значення <code>goals.focus</code> — не впливає на розрахунок Індексу зараз, але
      зберігається для подальшого використання (визначає, яку рекомендацію показати користувачу
      далі).
    </Typography>
  </LogicPanel>
);

// Мапінг goal → гілка AmrCalculator.getInterpretation().goals (2026-08-20) — ehi/plan.md п. 7
// фіксує це як ВІДКРИТИЙ блокер (мапінгу в бекенд-коді немає, `AmrCalculator` дає лише 3 гілки на
// 6 значень Goal/MainGoal). За прямою вказівкою користувача "роби все, я ж сказав" — власний вибір
// симуляції, щоб довести розрахунок до кінця, НЕ рішення продукту; підлягає перегляду зі
// стейкхолдерами. moderate (не active) скрізь — консервативніший, безпечніший дефолт.
const GOAL_TO_AMR_BRANCH: Record<Goal, (goals: any) => number> = {
  weight_loss: (g) => g.weightLoss.moderate.value,
  maintenance: (g) => g.maintenance.value,
  wellbeing: (g) => g.maintenance.value,
  muscle_gain: (g) => g.weightGain.moderate.value,
  endurance: (g) => g.maintenance.value,
  weight_gain: (g) => g.weightGain.moderate.value,
};
// Той самий вибір, що й GOAL_TO_AMR_BRANCH, але як текстовий лейбл для UI (2026-08-20) — окремою
// константою, щоб не виводити гілку в JSX через ternary (ризик розсинхронізації з мапінгом вище).
const GOAL_TO_AMR_BRANCH_LABEL: Record<Goal, string> = {
  weight_loss: 'weightLoss.moderate',
  maintenance: 'maintenance',
  wellbeing: 'maintenance',
  muscle_gain: 'weightGain.moderate',
  endurance: 'maintenance',
  weight_gain: 'weightGain.moderate',
};

// Мапінг goal → AmdrCalculator scheme (2026-08-20) — той самий статус: власний вибір симуляції,
// не рішення продукту. Схеми й описи — `diet-scheme.enum.ts`: PROTEIN_HEAVY явно описана як "набір
// м'язової маси або сушка" (weight_loss/muscle_gain), CARB_HEAVY — "види спорту на витривалість"
// (endurance), решта — BALANCED/MIXED.
const GOAL_TO_DIET_SCHEME: Record<Goal, string> = {
  weight_loss: 'protein_heavy',
  maintenance: 'balanced',
  wellbeing: 'balanced',
  muscle_gain: 'protein_heavy',
  endurance: 'carb_heavy',
  weight_gain: 'mixed',
};

// "Денні цілі" (2026-08-20, за прямою вказівкою користувача — "це все денні цілі", "формуємо
// логічні структури даних, бо інакше ти ніколи не збереш все це до купи") — одна логічна
// структура для всіх 8 значень, які клієнт досягає ЩОДНЯ (перераховуються/задаються на кожен
// день), на відміну від Focus/MainGoal (встановлюються один раз при онбордингу, не денні). Назва
// полів — реальні колонки сутності DailyNorm (`daily-norm.entity.ts`) + узгоджений
// ClientGoal-неймінг tasks/feelings (ehi/plan.md п. 6/8). caloriesIntake/proteinG/carbsG/fatG/
// caloriesBurned/waterMl — справжні виклики calculated-metrics; tasks/feelings — аксіома-дефолт
// 1/1, не розрахунок. BMR/AMR — НЕ частина DailyNorms: проміжні входи для caloriesBurned, самі не
// цілі клієнта.
type DailyNorms = {
  caloriesIntake: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  caloriesBurned: number;
  waterMl: number;
  tasks: number;
  feelings: number;
};

// Усе в ОДНІЙ структурі DailyNorms (2026-08-20, за проханням користувача — "формуємо логічні
// структури даних, бо інакше ти ніколи не збереш все це до купи"), один ефект, один Promise-ланцюг:
// BMR → AMR (гілка за GOAL_TO_AMR_BRANCH) → AMDR (схема за GOAL_TO_DIET_SCHEME) → WDNA, паралельно
// де можливо. Єдиний свідомий виняток із правила "Симуляція не робить запитів" — реальні виклики
// calculated-metrics, не JS-копії формул. `gender: 'unspecified'` — костиль (effectiveGender
// нижче): рішення стейкхолдерів підставляти female, BMR/WDNA не мають коефіцієнта для цього
// варіанту (технічний борг — правильне місце цій логіці в самих калькуляторах, не тут).
//
// Хук, не власний стан Phase2ResultCard (2026-08-20) — підсумковий блок після PHASE 2 ("резюмуємо
// які дані ми зібрали та які цілі отримали") теж потребує цих значень; піднято на рівень
// SimulationTab і викликається ОДИН раз, щоб не дублювати ті самі 4 виклики calculated-metrics.
function useDailyNorms(profile: ClientProfile) {
  const [dailyNorms, setDailyNorms] = useState<DailyNorms | null>(null);
  // BMR/AMR — НЕ частина DailyNorms (проміжні входи, не цілі клієнта), тримаються окремо.
  const [bmrKcal, setBmrKcal] = useState<number | null>(null);
  const [amrKcal, setAmrKcal] = useState<number | null>(null);
  // Готові пояснювальні рядки прямо з відповідей калькуляторів (2026-08-20, за питанням
  // користувача "калькулятор може самостійно повернути деталізований розрахунок?" — так, може) —
  // не переписуємо формули вручну текстом.
  const [bmrExplanation, setBmrExplanation] = useState<string | null>(null);
  const [amrExplanation, setAmrExplanation] = useState<string | null>(null);
  const [amdrExplanation, setAmdrExplanation] = useState<string | null>(null);
  const [waterFormula, setWaterFormula] = useState<string | null>(null);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setGoalsLoading(true);
    setGoalsError(null);
    // Скидаємо застарілі значення попереднього клієнта (2026-08-20, кодревью — без цього
    // Phase2SummaryBlock/експорт звіту встигали показати DailyNorms попереднього клієнта під
    // заголовком нового, поки новий Promise-ланцюг ще виконується).
    setDailyNorms(null);
    setBmrKcal(null);
    setAmrKcal(null);
    setBmrExplanation(null);
    setAmrExplanation(null);
    setAmdrExplanation(null);
    setWaterFormula(null);
    // Костиль для gender: 'unspecified' (2026-08-20, рішення стейкхолдерів — "підійшло рішення по
    // статі… підставляти жіночу"): BMR/WDNA не мають коефіцієнта для цього варіанту, тож замість
    // блокування розрахунку підставляємо 'female'. СВІДОМО тимчасове рішення саме тут (фронтова
    // симуляція) — правильне місце для цієї логіки самі калькулятори (api/docs/technical-debt.md,
    // новий пункт), не UI, що їх викликає; зафіксовано технічним боргом.
    const effectiveGender = profile.gender === 'unspecified' ? 'female' : profile.gender;
    const bmrParams = { gender: effectiveGender, age: profile.age, height: profile.height, weight: profile.weight };
    Promise.all([
      calculateMetric('bmr', bmrParams),
      calculateMetric('amr', { ...bmrParams, activityLevel: profile.activityLevel }),
      calculateMetric('wdna', { weight: profile.weight, gender: effectiveGender, age: profile.age, activityLevel: profile.activityLevel }),
    ])
      .then(([bmrRes, amrRes, wdnaRes]) => {
        if (cancelled) return null;
        const bmr = Math.round(bmrRes.result);
        const amr = Math.round(amrRes.result);
        setBmrKcal(bmr);
        setAmrKcal(amr);
        setBmrExplanation(bmrRes.explanation ?? null);
        setAmrExplanation(amrRes.explanation ?? null);
        setWaterFormula(wdnaRes.resultDetails?.calculationBreakdown?.formula ?? null);

        const calories = GOAL_TO_AMR_BRANCH[profile.goal](amrRes.resultDetails.goals);
        return calculateMetric('amdr', { amr: calories, scheme: GOAL_TO_DIET_SCHEME[profile.goal] })
          .then((amdrRes) => ({ bmr, amr, calories, waterMl: wdnaRes.result, amdrRes }));
      })
      .then((combined) => {
        if (cancelled || !combined) return;
        setDailyNorms({
          caloriesIntake: Math.round(combined.calories),
          proteinG: combined.amdrRes.result.protein,
          carbsG: combined.amdrRes.result.carbs,
          fatG: combined.amdrRes.result.fat,
          caloriesBurned: combined.amr - combined.bmr,
          waterMl: combined.waterMl,
          tasks: 1,
          feelings: 1,
        });
        setAmdrExplanation(combined.amdrRes.explanation ?? null);
      })
      .catch((err) => {
        if (!cancelled) setGoalsError(getErrorMessage(err, 'Не вдалося порахувати денні цілі'));
      })
      .finally(() => {
        if (!cancelled) setGoalsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile.weight, profile.gender, profile.age, profile.height, profile.activityLevel, profile.goal]);

  return { dailyNorms, bmrKcal, amrKcal, bmrExplanation, amrExplanation, amdrExplanation, waterFormula, goalsLoading, goalsError };
}

type DailyNormsState = ReturnType<typeof useDailyNorms>;

// Personal Plan (PHASE 2, результат) — task.md прямо каже "не показувати як повністю
// персоналізований до завершення required-полів Phase 2" — тому підпис-застереження внизу картки.
// scenario-проп прибрано (2026-08-20) — усі 6 значень картки тепер лічені наживо з profile, жодне
// не бере даних зі статичного scenario.data. DailyNorms-значення тепер приходять окремими пропами
// з useDailyNorms, викликаного в SimulationTab (2026-08-21) — не дублюємо виклики
// calculated-metrics для підсумкового блоку нижче, якому потрібні ті самі значення.
const Phase2ResultCard: React.FC<{ profile: ClientProfile } & DailyNormsState> = ({
  profile, dailyNorms, bmrKcal, amrKcal, bmrExplanation, amrExplanation, amdrExplanation, waterFormula, goalsLoading, goalsError,
}) => {
  // Тимчасово не читаються — тултіпи з поясненнями закоментовані нижче (2026-08-26).
  void bmrExplanation; void amrExplanation; void amdrExplanation; void waterFormula;
  return (
    <LogicPanel>
      {/* Реальний екран Figma — назва шару буквально "page:onboarding:personal plan" (з пробілом,
          не дефісом — так у файлі, не виправлено). Скріншот наданий користувачем напряму (PNG),
          не через Figma MCP (rate-limit ще діяв) — збережено як /onboarding/phase2-personal-plan.png.
          Картки: Ціль по харчуванню (калорії + Білки/Вуглеводи/Жири, велика картка) → пара Норма
          води/Активність → пара Задачі/Відчуття. Б/Ж/В попередньо помилково вважались відсутніми
          в дизайні (наплутано з lorem-плейсхолдерами в CSS-експорті) — виправлено після скріна. */}
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Box component="img" src="/onboarding/phase2-personal-plan.png" alt="page:onboarding:personal plan" sx={{ width: 100, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        <PageNameTag name="page:onboarding:personal plan" sx={{ mt: 0.5 }} />
      </Box>
      <Typography variant="caption" color="text.secondary">«Ваш персональний план готовий!»</Typography>

      {/* Наступний екран одразу після "Ваш персональний план готовий!" (task.md: Personal Plan →
          «Створіть свій перший день» → Home) — інформаційна картка, завершує PHASE 2, нічого не
          збирає й не рахує (2026-08-20, скрін наданий користувачем напряму, /onboarding/phase3-first-day.png).
          Розміщена тут (не окремим блоком/фазою) — за проханням користувача, бо логічно це
          продовження саме цього екрана Personal Plan, а не PHASE 3. */}
      <Box sx={{ textAlign: 'center', my: 1.5 }}>
        <Box component="img" src="/onboarding/phase3-first-day.png" alt="page:onboarding:first-day" sx={{ width: 100, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        <PageNameTag name="page:onboarding:first-day" sx={{ mt: 0.5 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, maxWidth: 320, mx: 'auto' }}>
          «Створіть свій перший день» — 3 кроки (Плануйте / Виконуйте / Аналізуйте), даних не
          збирає, лише веде далі в Home.
        </Typography>
      </Box>

      {/* Дві окремі таблиці (2026-08-20, за проханням користувача — "допоміжний розрахунок bmr
          треба винести в окрему таблицю чи блок, візуально"): "Допоміжні розрахунки" (bmr/amr,
          сірі бульбашки — не поля DailyNorms, лише входи) і "Денні цілі" (goals.caloriesIntake/
          proteinG/carbsG/fatG/caloriesBurned/waterMl/tasks/feelings — зелені, реальні цілі
          клієнта). Неймінг колонки "Поле" — за проханням користувача "зроби їм усім правильний
          неймінг": caloriesIntake/proteinG/carbsG/fatG/caloriesBurned/waterMl — реальні назви
          колонок сутності DailyNorm (`api/src/modules/settings/entities/daily-norm.entity.ts`), не
          вигадані; bmr/amr — `name` самих калькуляторів; goals.tasks/goals.feelings — узгоджений
          неймінг ClientGoal (ehi/plan.md п. 6/8), дефолт 1/1, не розрахунок. */}
      {/* Допоміжні розрахунки — окремо, візуально (2026-08-20, за проханням користувача — "це
          допоміжний розрахунок bmr, треба винести в окрему таблицю чи блок, візуально"). BMR і AMR
          НЕ є полями DailyNorms — це проміжні входи для goals.caloriesBurned/goals.caloriesIntake
          нижче, не цілі клієнта самі по собі. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Допоміжні розрахунки (не цілі клієнта, лише входи для таблиці нижче)
      </Typography>
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Поле</TableCell>
              <TableCell>Значення</TableCell>
              <TableCell>Чому</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>bmr</TableCell>
              <TableCell>
                {goalsLoading && <CircularProgress size={16} />}
                {!goalsLoading && goalsError && (
                  <Typography variant="caption" color="error">{goalsError}</Typography>
                )}
                {!goalsLoading && !goalsError && bmrKcal !== null && (
                  /* Сіра бульбашка (2026-08-20, за проханням користувача) — конвенція: BMR тут
                     проміжний розрахунок (вхід для AMR/caloriesBurned), не частина DailyNorms і не
                     кінцеве значення "Персонального плану" з дизайну — сірий відрізняє від зелених
                     кінцевих результатів. */
                  <Chip label={`${bmrKcal} ккал`} size="small" sx={{ fontWeight: 700, bgcolor: 'grey.300' }} />
                )}
                {!goalsLoading && !goalsError && bmrKcal === null && (
                  <Chip label="N/A" size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Розраховано калькулятором{' '}
                <Link component={RouterLink} to="/calculated-metrics?tab=bmr">BMR</Link> (базовий
                метаболізм — енергія в стані спокою).{' '}
                {/* Тимчасово закоментовано (2026-08-26) — див. коментар вище в Phase1LogicPanel.
                <Tooltip arrow title={bmrExplanation ?? (goalsLoading ? 'Рахується…' : 'Недоступно')}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle' }} />
                </Tooltip>
                */}
                <br />
                Поля: <code>gender</code>/<code>age</code>/
                <code>height</code>/<code>weight</code>.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>amr</TableCell>
              <TableCell>
                {goalsLoading && <CircularProgress size={16} />}
                {!goalsLoading && goalsError && (
                  <Typography variant="caption" color="error">{goalsError}</Typography>
                )}
                {!goalsLoading && !goalsError && amrKcal !== null && (
                  <Chip label={`${amrKcal} ккал`} size="small" sx={{ fontWeight: 700, bgcolor: 'grey.300' }} />
                )}
                {!goalsLoading && !goalsError && amrKcal === null && (
                  <Chip label="N/A" size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Розраховано калькулятором{' '}
                <Link component={RouterLink} to="/calculated-metrics?tab=amr">AMR</Link> = BMR ×
                коефіцієнт активності (загальні добові витрати).{' '}
                {/* Тимчасово закоментовано (2026-08-26) — див. коментар вище в Phase1LogicPanel.
                <Tooltip arrow title={amrExplanation ?? (goalsLoading ? 'Рахується…' : 'Недоступно')}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle' }} />
                </Tooltip>
                */}
                <br />
                Поля: <code>gender</code>/<code>age</code>/<code>height</code>/<code>weight</code>/
                <code>activityLevel</code>.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Денні цілі (DailyNorms)
      </Typography>
      <TableContainer sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Поле</TableCell>
              <TableCell>Значення</TableCell>
              <TableCell>Чому</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.caloriesIntake</TableCell>
              <TableCell>
                {goalsLoading && <CircularProgress size={16} />}
                {!goalsLoading && goalsError && (
                  <Typography variant="caption" color="error">{goalsError}</Typography>
                )}
                {!goalsLoading && !goalsError && dailyNorms !== null && (
                  <Chip label={`${dailyNorms.caloriesIntake} ккал`} color="success" size="small" sx={{ fontWeight: 700 }} />
                )}
                {!goalsLoading && !goalsError && dailyNorms === null && (
                  <Chip label="N/A" size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Розраховано калькулятором{' '}
                <Link component={RouterLink} to="/calculated-metrics?tab=amr">AMR</Link>, гілка за{' '}
                <code>goal</code> (зараз "{GOAL_LABELS[profile.goal]}" → гілка{' '}
                <code>{GOAL_TO_AMR_BRANCH_LABEL[profile.goal]}</code>).{' '}
                {/* Тимчасово закоментовано (2026-08-26) — див. коментар вище в Phase1LogicPanel.
                <Tooltip arrow title={amrExplanation ?? (goalsLoading ? 'Рахується…' : 'Недоступно')}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle' }} />
                </Tooltip>
                */}
                <br />
                Мапінг <code>goal → гілка AMR</code> — власний вибір симуляції, не рішення продукту
                (ehi/plan.md п. 7).
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.proteinG, goals.carbsG, goals.fatG</TableCell>
              <TableCell>
                {goalsLoading && <CircularProgress size={16} />}
                {!goalsLoading && goalsError && (
                  <Typography variant="caption" color="error">{goalsError}</Typography>
                )}
                {!goalsLoading && !goalsError && dailyNorms !== null && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={`Б: ${dailyNorms.proteinG} г`} color="success" size="small" />
                    <Chip label={`В: ${dailyNorms.carbsG} г`} color="success" size="small" />
                    <Chip label={`Ж: ${dailyNorms.fatG} г`} color="success" size="small" />
                  </Box>
                )}
                {!goalsLoading && !goalsError && dailyNorms === null && (
                  <Chip label="N/A" size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Розраховано калькулятором{' '}
                <Link component={RouterLink} to="/calculated-metrics?tab=amdr">AMDR</Link>, схема за{' '}
                <code>goal</code> (зараз "{GOAL_LABELS[profile.goal]}" → схема{' '}
                <code>{GOAL_TO_DIET_SCHEME[profile.goal]}</code>).{' '}
                {/* Тимчасово закоментовано (2026-08-26) — див. коментар вище в Phase1LogicPanel.
                <Tooltip arrow title={amdrExplanation ?? (goalsLoading ? 'Рахується…' : 'Недоступно')}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle' }} />
                </Tooltip>
                */}
                <br />
                Мапінг <code>goal → scheme</code> — власний вибір симуляції, не рішення продукту
                (ehi/plan.md п. 7).
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.caloriesBurned</TableCell>
              <TableCell>
                {goalsLoading && <CircularProgress size={16} />}
                {!goalsLoading && goalsError && (
                  <Typography variant="caption" color="error">{goalsError}</Typography>
                )}
                {!goalsLoading && !goalsError && dailyNorms !== null && (
                  <Chip label={`${dailyNorms.caloriesBurned} ккал`} color="success" size="small" sx={{ fontWeight: 700 }} />
                )}
                {!goalsLoading && !goalsError && dailyNorms === null && (
                  <Chip label="N/A" size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Похідне від метаболізму — <code>amr − bmr</code> (рядки вище), не окремий
                калькулятор: компонент фізичної активності всередині AMR.{' '}
                {/* Тимчасово закоментовано (2026-08-26) — див. коментар вище в Phase1LogicPanel.
                <Tooltip arrow title={amrExplanation ?? (goalsLoading ? 'Рахується…' : 'Недоступно')}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle' }} />
                </Tooltip>
                */}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.waterMl</TableCell>
              <TableCell>
                {/* Зелена бульбашка (2026-08-20, за проханням користувача) — конвенція: зелений =
                    реальна калькуляція (справжній API-виклик), синій = мок/дефолт/введене вручну. */}
                {goalsLoading && <CircularProgress size={16} />}
                {!goalsLoading && goalsError && (
                  <Typography variant="caption" color="error">{goalsError}</Typography>
                )}
                {!goalsLoading && !goalsError && dailyNorms !== null && (
                  <Chip label={`${dailyNorms.waterMl} мл`} color="success" size="small" sx={{ fontWeight: 700 }} />
                )}
                {!goalsLoading && !goalsError && dailyNorms === null && (
                  <Chip label="N/A" size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Розраховано калькулятором{' '}
                <Link component={RouterLink} to="/calculated-metrics?tab=wdna">WDNA</Link>{' '}
                {/* Тултіп із самим рядком розрахунку (2026-08-20) — беремо готовий рядок з
                    відповіді калькулятора (resultDetails.calculationBreakdown.formula), не
                    переписуємо формулу вручну.
                    Тимчасово закоментовано (2026-08-26, за проханням користувача). */}
                {/*
                <Tooltip
                  arrow
                  title={
                    waterFormula ?? (goalsLoading ? 'Рахується…' : 'Розрахунок недоступний (стать не вказана або сталася помилка)')
                  }
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle' }} />
                </Tooltip>
                */}
                <br />
                Розраховується на базі полів: <code>weight</code>/<code>gender</code>/
                <code>age</code>/<code>activityLevel</code>.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.main</TableCell>
              <TableCell>
                <Chip label={GOAL_LABELS[profile.goal]} color="primary" size="small" sx={{ fontWeight: 700 }} />
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Вибір клієнта (Основна ціль, PHASE 2), не розрахунок — саме це поле визначає гілку
                AMR/схему AMDR у рядках вище.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.tasks</TableCell>
              <TableCell>
                {dailyNorms !== null && (
                  <Chip label={dailyNorms.tasks} color="primary" size="small" sx={{ fontWeight: 700 }} />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Дефолт MVP, не розрахунок (calculated-metrics цього не рахує)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.feelings</TableCell>
              <TableCell>
                {dailyNorms !== null && (
                  <Chip label={dailyNorms.feelings} color="primary" size="small" sx={{ fontWeight: 700 }} />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Дефолт MVP, не розрахунок (calculated-metrics цього не рахує)
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

<Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
        «Досягнення цих цілей допоможе підвищити ваш Індекс здоров'я.»
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Не показувати як повністю персоналізований до завершення Phase 2.
      </Typography>
    </LogicPanel>
  );
};

// Заготовка полів PHASE 3 (2026-08-22, за проханням користувача — "хочу зробити спочатку
// заготовку з даними, які ми хочемо отримати від користувача", "як і раніше — працюємо з
// прототипом"): та сама таблиця Поле/Значення, що й у решти карток сторінки — технічна назва
// поля зафіксована як референс для майбутньої реалізації, навіть без калькулятора/дизайну позаду
// (task.md рядки 187-207: сон/препарати/додаткові цілі/улюблені активності/антропометрія/інше).
const Phase3Card: React.FC<{ scenario: (typeof CLIENT_SCENARIOS)[number] }> = ({ scenario }) => {
  const { phase3 } = scenario;
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{scenario.label}</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Поле</TableCell>
              <TableCell>Значення</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                Препарати (список, кожен окремим рядком)
                <Typography display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  phase3.medications
                </Typography>
              </TableCell>
              <TableCell>
                {phase3.medications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Немає активних препаратів</Typography>
                ) : phase3.medications.map((m) => (
                  <Chip key={m} label={m} color="primary" size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Додаткові цілі (список)
                <Typography display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  phase3.additionalGoals
                </Typography>
              </TableCell>
              <TableCell>
                {phase3.additionalGoals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                ) : phase3.additionalGoals.map((g) => (
                  <Chip key={g} label={g} color="primary" size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Улюблені активності (multi-select)
                <Typography display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  phase3.favoriteActivities
                </Typography>
              </TableCell>
              <TableCell>
                {phase3.favoriteActivities.map((a) => (
                  <Chip key={a} label={a} color="primary" size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Додаткова антропометрія (список параметрів, юзер сам додає)
                <Typography display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  phase3.additionalAnthropometry
                </Typography>
              </TableCell>
              <TableCell>
                {phase3.additionalAnthropometry.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                ) : phase3.additionalAnthropometry.map((m) => (
                  <Chip key={m.label} label={`${m.label}: ${m.value}`} color="primary" size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Інші дані персоналізації
                <Typography display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  phase3.otherData
                </Typography>
              </TableCell>
              <TableCell>{phase3.otherData}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const Phase3LogicPanel: React.FC = () => (
  <LogicPanel>
    <Typography variant="body2">
      Ці поля PHASE 3 — інформація до відома, ніде не розраховується. Просто записується в
      профіль клієнта, без виклику жодного калькулятора. НЕ впливає на розрахунок Ресурсу/Індексу —
      лише на майбутні рекомендації. Донабирається прогресивно через in-app prompt / push (не
      раніше ~24 год, не push щодня), не блокує перший вхід у застосунок.
    </Typography>
  </LogicPanel>
);

// Сон — незалежний 2-колоночний блок PHASE 3 (2026-08-20, за проханням користувача — "ми ж
// домовилися що в фазі 3 у нас багато незалежних блоків (2-колоночних)", "зараз ти зробив один
// контейнер справа для всіх"). Перший з майбутніх окремих блоків PHASE 3 (решта 5 полів поки в
// спільних Phase3Card/Phase3LogicPanel — сон витягнуто першим як прецедент). Права панель — той
// самий стиль Поле/Значення/Чому, що й `goals.tasks`/`goals.main` у Phase2ResultCard.
const Phase3SleepCard: React.FC<{ scenario: (typeof CLIENT_SCENARIOS)[number] }> = ({ scenario }) => {
  const { phase3 } = scenario;
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
      {/* Без мітки фази/клієнта тут (2026-08-20, за проханням користувача) — це вже є в спільному
          заголовку секції PHASE 3 вище ("PHASE 3 — Progressive profile completion — {label}");
          незалежні блоки нижче — самі лише опитувальні питання, без повторення контексту. */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Сон</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Поле</TableCell>
              <TableCell>Значення</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                «Скільки годин на добу ви зазвичай спите?»
                <Typography display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  phase3.sleepFactHours
                </Typography>
              </TableCell>
              <TableCell><Chip label={`${phase3.sleepFactHours} год`} color="primary" size="small" variant="outlined" /></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                «Скільки годин на добу ви хочете спати?»
                <Typography display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  goals.sleepTargetHours
                </Typography>
              </TableCell>
              <TableCell><Chip label={`${phase3.sleepTargetHours} год`} color="primary" size="small" variant="outlined" /></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const Phase3SleepLogicPanel: React.FC<{ scenario: (typeof CLIENT_SCENARIOS)[number] }> = ({ scenario }) => {
  const { phase3 } = scenario;
  return (
    <LogicPanel>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Поле</TableCell>
              <TableCell>Значення</TableCell>
              <TableCell>Чому</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>goals.sleepTargetHours</TableCell>
              <TableCell>
                <Chip label={`${phase3.sleepTargetHours} год`} color="primary" size="small" sx={{ fontWeight: 700 }} />
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Реальний параметр <code>sleep.targetHours</code> майбутнього{' '}
                <Link component={RouterLink} to="?tab=inputs">daily-resource</Link> калькулятора —
                стане входом розрахунку S/F, коли з'явиться реальне логування днів. Зараз лише
                записується, не розраховується.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>phase3.sleepFactHours</TableCell>
              <TableCell>
                <Chip label={`${phase3.sleepFactHours} год`} size="small" variant="outlined" />
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Лише до відома. НІКОЛИ не підставляється як щоденний факт для калькулятора — ТЗ
                прямо забороняє (§4.5: "якщо тривалість до завершення дня не внесена, S = 0").
                Розібрано в{' '}
                <code>workspace/current-work/ehi/sleep-open-questions.md</code>, п.6.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </LogicPanel>
  );
};

const Phase4Card: React.FC<{ scenario: (typeof CLIENT_SCENARIOS)[number] }> = ({ scenario }) => {
  const { phase4 } = scenario;
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{scenario.label}</Typography>
      <Typography variant="body2">Apple Health: {phase4.appleHealthConnected ? 'підключено' : 'не підключено'}</Typography>
      <Typography variant="body2">Антропометрія: {phase4.additionalAnthropometry}</Typography>
      <Typography variant="body2">Інші health-параметри: {phase4.otherHealthParams}</Typography>
    </Box>
  );
};

const Phase4LogicPanel: React.FC = () => (
  <LogicPanel>
    <Typography variant="body2">
      Поза межами обов'язкового onboarding — не показується в цьому флоу взагалі, запитується
      контекстно в різних розділах застосунку (Apple Health — при підключенні джерел,
      антропометрія — у своєму розділі).
    </Typography>
  </LogicPanel>
);

// Підсумок PHASE 0-2 (2026-08-21, за проханням користувача — "після фази 2 зробимо відступ та
// вставимо один окремий блок де резюмуємо які дані ми зібрали та які цілі отримали"). Дві
// половини: зібрані ДАНІ (6 самооцінок + Focus + профіль — усе, що ввів клієнт) і отримані ЦІЛІ
// (DailyNorms — усе, що порахували з цих даних). PHASE 0-2 — юзерфлоу з чітким розумінням "що
// збираємо і як рахуємо"; PHASE 3/4 нижче — опційні, не впливають на базову функціональність MVP.
const Phase2SummaryBlock: React.FC<{ scenario: ClientScenario; profile: ClientProfile } & DailyNormsState> = ({
  scenario, profile, dailyNorms,
}) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'action.hover' }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
      ✅ PHASE 0-2 завершено: {scenario.label}
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Дані, які зібрали</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Назва</TableCell>
                <TableCell>Значення</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DIRECTIONS.map((d) => (
                <TableRow key={d}>
                  <TableCell>{DIRECTION_LABELS[d]}</TableCell>
                  <TableCell><Chip label={scenario.data.selfAssessment[d]} color="primary" size="small" variant="outlined" /></TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>Focus</TableCell>
                <TableCell><Chip label={FOCUS_LABELS[scenario.onboardingFocus]} color="primary" size="small" variant="outlined" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Стать</TableCell>
                <TableCell><Chip label={GENDER_LABELS[profile.gender]} color="primary" size="small" variant="outlined" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Вік</TableCell>
                <TableCell><Chip label={`${profile.age} р.`} color="primary" size="small" variant="outlined" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Зріст</TableCell>
                <TableCell><Chip label={`${profile.height} см`} color="primary" size="small" variant="outlined" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Вага</TableCell>
                <TableCell><Chip label={`${profile.weight} кг`} color="primary" size="small" variant="outlined" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Рівень активності</TableCell>
                <TableCell><Chip label={ACTIVITY_LEVEL_LABELS[profile.activityLevel]} color="primary" size="small" variant="outlined" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Основна ціль</TableCell>
                <TableCell><Chip label={GOAL_LABELS[profile.goal]} color="primary" size="small" variant="outlined" /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Цілі, які отримали</Typography>
        {dailyNorms === null ? (
          <Typography variant="body2" color="text.secondary">Рахується…</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Значення</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Калорії</TableCell>
                  <TableCell><Chip label={`${dailyNorms.caloriesIntake} ккал`} color="success" size="small" variant="outlined" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Білки</TableCell>
                  <TableCell><Chip label={`${dailyNorms.proteinG} г`} color="success" size="small" variant="outlined" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Вуглеводи</TableCell>
                  <TableCell><Chip label={`${dailyNorms.carbsG} г`} color="success" size="small" variant="outlined" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Жири</TableCell>
                  <TableCell><Chip label={`${dailyNorms.fatG} г`} color="success" size="small" variant="outlined" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Активність</TableCell>
                  <TableCell><Chip label={`${dailyNorms.caloriesBurned} ккал`} color="success" size="small" variant="outlined" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Вода</TableCell>
                  <TableCell><Chip label={`${dailyNorms.waterMl} мл`} color="success" size="small" variant="outlined" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Задачі</TableCell>
                  <TableCell><Chip label={dailyNorms.tasks} color="primary" size="small" variant="outlined" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Відчуття</TableCell>
                  <TableCell><Chip label={dailyNorms.feelings} color="primary" size="small" variant="outlined" /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  </Box>
);

const SimulationTab: React.FC = () => {
  const [profiles, setProfiles] = useState<ClientProfile[]>(() => CLIENT_SCENARIOS.map((s) => s.profile));
  // Перемикач клієнта (2026-08-21, за проханням користувача — "для економії місця показувати в
  // 1 момент одного клієнта"): рендеримо картки лише для обраного, а не всі 3 одночасно в кожній
  // фазі — сторінка виросла до 6 секцій, 3 колонки в кожній була занадто довгою.
  const [activeIndex, setActiveIndex] = useState(0);
  const scenario = CLIENT_SCENARIOS[activeIndex];
  // Один виклик на всю вкладку (2026-08-21) — і Phase2ResultCard, і підсумковий блок після
  // PHASE 2 показують ті самі DailyNorms-значення, рахуємо їх раз, не двічі.
  const dailyNormsState = useDailyNorms(profiles[activeIndex]);

  // Модалка-конфігуратор симулянтів (2026-08-21, за проханням користувача — "заходиш в модалку і
  // там налаштовуєш наших 5 симулянтів як хочеш, зберігається в пам'яті в json"): редагування
  // сирого JSON усіх профілів прямо в модалці (BaseDialog, за конвенцією проєкту), не лише
  // файловий roundtrip. Working set — редагований `profiles` (той, що редагує ProfileCard), НЕ
  // сам health-index-client-scenarios.json (код-рівневий дефолт, "Скинути до дефолту" повертає
  // саме до нього).
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const [jsonDraft, setJsonDraft] = useState('');
  const [jsonDraftError, setJsonDraftError] = useState<string | null>(null);
  const importClientsInputRef = useRef<HTMLInputElement>(null);

  const openConfigurator = () => {
    setJsonDraft(JSON.stringify(profiles, null, 2));
    setJsonDraftError(null);
    setConfiguratorOpen(true);
  };

  const applyJsonDraft = () => {
    try {
      const parsed = JSON.parse(jsonDraft);
      if (!Array.isArray(parsed) || parsed.length !== profiles.length) {
        throw new Error(`Очікується масив із ${profiles.length} профілів клієнтів`);
      }
      const validated = parsed.map((item, i) => validateClientProfile(item, i));
      setProfiles(validated);
      setJsonDraftError(null);
      toastService.success('Профілі клієнтів застосовано');
      setConfiguratorOpen(false);
    } catch (err) {
      setJsonDraftError(getErrorMessage(err, 'невідома помилка'));
    }
  };

  const handleExportProfiles = () => {
    const blob = new Blob([jsonDraft], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `simulation-clients-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportProfiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setJsonDraft(String(reader.result));
      setJsonDraftError(null);
    };
    reader.readAsText(file);
  };

  // Звіт поточної симуляції (2026-08-21, за проханням користувача — "експорт звіту поточної
  // симуляції де будуть дані симулянта, потім фази і потім результуючі дані", "текстовий файл це
  // жорстоко, зроби звіт гарнішим"): HTML, той самий стиль/патерн, що вже є для друкованої анкети
  // (`buildQuestionnaireHtml`/`openQuestionnaireDocument` вище) — відкривається в новій вкладці
  // (не .txt-файл), можна роздрукувати чи зберегти як PDF через браузер. Дані клієнта (первинні,
  // "сирі" — профіль + самооцінка) → фази (1/1.1/2/3/4) → результуючі дані (DailyNorms). Стартовий
  // Індекс для звіту — окремий одноразовий виклик `baseline-index` саме в момент експорту (за явним
  // кліком користувача, не фоновий запит) — Phase1LogicPanel рахує його у власному локальному
  // стані, не піднятому нагору, тож простіше порахувати ще раз, ніж рефакторити заради звіту.
  const [reportExporting, setReportExporting] = useState(false);
  const handleExportReport = async () => {
    setReportExporting(true);
    try {
      const p = profiles[activeIndex];
      const dg = dailyNormsState.dailyNorms;
      // Захист від кодревью-знахідки: DailyNorms скидається в null на старті нового запиту при
      // зміні клієнта (2026-08-20) — швидкий клік на "Звіт по клієнту" саме в цей момент раніше
      // міг або впасти на dg.caloriesIntake (null), або (до скидання) вставити в звіт застарілі
      // цифри попереднього клієнта. Кнопка нижче теж дизейблиться на goalsLoading — це другий шар.
      if (dg === null) {
        toastService.error('Денні цілі ще рахуються — зачекайте секунду і спробуйте ще раз.');
        return;
      }
      const baselineRes = await calculateHealthIndex('baseline-index', { selfAssessment: scenario.data.selfAssessment });
      const { waterFormula } = dailyNormsState;
      const escape = (s: string) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const row = (label: string, value: string | number) => `
        <div class="field">
          <span class="field-label">${escape(label)}</span>
          <span class="field-value"><strong>${escape(String(value))}</strong></span>
        </div>`;
      // "Що отримали" (2026-08-21, за проханням користувача — "в звіті має бути зрозуміло що
      // прийшло що отримали та яким чином", "по-людськи вивести всі значення які отримуються з
      // їх неймінгом") — назва поля (goals.X, той самий неймінг, що в таблиці на сторінці) +
      // значення + яким калькулятором/формулою порахували.
      const goalRow = (fieldName: string, value: string, how: string, chipClass = 'goal-chip') => `
        <div class="goal-row">
          <code class="field-name">${escape(fieldName)}</code>
          <span class="${chipClass}">${escape(value)}</span>
          <div class="how">${escape(how)}</div>
        </div>`;

      // Кожна фаза — 2 колонки (2026-08-21, за проханням користувача — "зроби кроки зрозуміло
      // (права ліва частина) що надійшло що отримали"): те саме ліва/права дихотомія, що вже є на
      // самій сторінці Симуляції (зліва рав-дані, справа логіка/розрахунок), перенесено у звіт.
      const phaseSection = (title: string, leftTitle: string, leftHtml: string, rightTitle: string, rightHtml: string) => `
  <h2>${escape(title)}</h2>
  <div class="phase-columns">
    <div class="col col-input">
      <div class="col-title">${escape(leftTitle)}</div>
      ${leftHtml}
    </div>
    <div class="col col-output">
      <div class="col-title">${escape(rightTitle)}</div>
      ${rightHtml}
    </div>
  </div>`;

      const html = `<!doctype html>
<html lang="uk">
<head>
<meta charset="utf-8" />
<title>Звіт симуляції — ${escape(scenario.label)}</title>
<style>
  /* 2026-08-21, за проханням користувача "зроби звіт більше схожим на нашу сторінку симуляції" —
     та сама візуальна мова, що й на самій сторінці: рав-дані (ліва колонка кожної фази) —
     суцільна рамка (як картки PHASE-секцій), логіка/розрахунок (права) — пунктирна рамка + сірий
     фон (той самий принцип, що й спільний LogicPanel-компонент сторінки). */
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; max-width: 900px; margin: 24px auto; color: #282828; background: #fff; }
  h1 { font-size: 22px; margin-bottom: 4px; }
  .subtitle { color: #666; font-size: 13px; margin-bottom: 24px; }
  h2 { font-size: 15px; font-weight: 700; margin-top: 32px; margin-bottom: 8px; }
  .phase-columns { display: flex; gap: 16px; align-items: stretch; }
  .col { flex: 1; min-width: 0; border-radius: 8px; padding: 12px 14px; }
  .col-input { border: 1px solid #d3d3d3; background: #fff; }
  .col-output { border: 1px dashed #b0b0b0; background: #f2f2f2; }
  .col-title { font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; color: #888; margin-bottom: 8px; }
  .field { display: flex; align-items: baseline; gap: 12px; padding: 5px 0; border-bottom: 1px dotted #ccc; }
  .field:last-child { border-bottom: none; }
  .field-label { flex: 0 0 40%; color: #555; }
  .field-value { flex: 1; text-align: right; }
  .goal-chip { display: inline-block; background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7; border-radius: 12px; padding: 2px 10px; margin: 2px; font-size: 13px; }
  .default-chip { display: inline-block; background: #e3f2fd; color: #1565c0; border: 1px solid #90caf9; border-radius: 12px; padding: 2px 10px; margin: 2px; font-size: 13px; }
  .empty { color: #999; font-style: italic; font-size: 13px; margin: 0; }
  .goal-row { padding: 6px 0; border-bottom: 1px dotted #ccc; }
  .goal-row:last-child { border-bottom: none; }
  .how { font-size: 11px; color: #888; margin-top: 2px; }
  .field-name { display: block; font-size: 10px; color: #aaa; margin-bottom: 2px; }
  .intro { background: #f9f9f9; border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px 16px; font-size: 13px; color: #444; margin-bottom: 24px; }
  @media print { body { margin: 0; } .phase-columns { break-inside: avoid; } }
</style>
</head>
<body>
  <h1>Звіт симуляції онбордингу — ${escape(scenario.label)}</h1>
  <p class="subtitle">Сформовано: ${escape(new Date().toLocaleString('uk-UA'))}</p>
  <p class="intro">
    Цей документ — симуляція проходження одним умовним клієнтом онбордингу Everyday (сценарій
    "${escape(scenario.label)}"). Для кожного кроку зліва показано, які дані вводить клієнт, справа
    — що застосунок з ними рахує (і за якою формулою/калькулятором) або зберігає без розрахунку.
  </p>

  <h2>Дані клієнта (первинні)</h2>
  <div class="col col-input" style="max-width: 440px;">
    ${row('Стать', GENDER_LABELS[p.gender])}
    ${row('Вік', `${p.age} р.`)}
    ${row('Зріст', `${p.height} см`)}
    ${row('Вага', `${p.weight} кг`)}
    ${row('Рівень активності', ACTIVITY_LEVEL_LABELS[p.activityLevel])}
    ${row('Основна ціль', GOAL_LABELS[p.goal])}
  </div>

  ${phaseSection(
    'PHASE 0 — Intro → Disclaimer → Registration',
    'Що надійшло',
    '<p class="empty">Нічого — клієнт лише отримує інформацію (Intro/Disclaimer), не вводить дані.</p>',
    'Що отримали',
    '<p class="empty">Нічого не розраховується й не зберігається на цьому кроці.</p>',
  )}

  ${phaseSection(
    'PHASE 1 — Starting Health Index (6 self-assessment)',
    'Що надійшло (самооцінка)',
    DIRECTIONS.map((d) => row(DIRECTION_LABELS[d], scenario.data.selfAssessment[d])).join(''),
    'Що отримали (розрахунок)',
    row('Стартовий Індекс (baseline-index)', baselineRes.result.startIndex.toFixed(1)),
  )}

  ${phaseSection(
    'PHASE 1.1 — Focus',
    'Що надійшло (вибір)',
    row('Focus', FOCUS_LABELS[scenario.onboardingFocus]),
    'Що отримали',
    `<p>Отримали значення <code>goals.focus</code> — не впливає на розрахунок Індексу зараз, але зберігається для подальшого використання.</p>`,
  )}

  ${phaseSection(
    'PHASE 2 — Goal + базова персоналізація',
    'Що надійшло',
    [
      row('Стать', GENDER_LABELS[p.gender]),
      row('Вік', `${p.age} р.`),
      row('Зріст', `${p.height} см`),
      row('Вага', `${p.weight} кг`),
      row('Рівень активності', ACTIVITY_LEVEL_LABELS[p.activityLevel]),
      row('Основна ціль', GOAL_LABELS[p.goal]),
    ].join(''),
    'Що отримали (Персональний план, DailyNorms)',
    dg
      ? [
          goalRow('goals.main', GOAL_LABELS[p.goal], 'Вибір клієнта (Основна ціль, PHASE 2) — визначає гілку AMR/схему AMDR нижче', 'default-chip'),
          goalRow('goals.caloriesIntake', `${dg.caloriesIntake} ккал`, `AMR, гілка "${GOAL_TO_AMR_BRANCH_LABEL[p.goal]}" (за goals.main="${p.goal}")`),
          goalRow('goals.proteinG / goals.carbsG / goals.fatG', `${dg.proteinG} г / ${dg.carbsG} г / ${dg.fatG} г`, `AMDR, схема "${GOAL_TO_DIET_SCHEME[p.goal]}" (за goals.main="${p.goal}")`),
          goalRow('goals.caloriesBurned', `${dg.caloriesBurned} ккал`, 'AMR − BMR (компонент фізактивності)'),
          goalRow('goals.waterMl', `${dg.waterMl} мл`, waterFormula ?? 'WDNA'),
          goalRow('goals.tasks', `${dg.tasks}`, 'Дефолт MVP, не розрахунок', 'default-chip'),
          goalRow('goals.feelings', `${dg.feelings}`, 'Дефолт MVP, не розрахунок', 'default-chip'),
        ].join('')
      : '<p class="empty">ще рахується або недоступно</p>',
  )}
</body>
</html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      toastService.error(`Не вдалося сформувати звіт: ${getErrorMessage(err, 'невідома помилка')}`);
    } finally {
      setReportExporting(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>Симуляція онбордингу</Typography>

      {/* Перемикач клієнта — на самий верх, стіккі при скролі (2026-08-21, за проханням
          користувача), щоб можна було перемикати клієнта в будь-якій точці довгої сторінки. */}
      {/* top: 52px — висота фіксованого TopBar (той самий spacer, що й у DashboardLayout), інакше
          sticky-рядок ліз би під нього при скролі. */}
      <Box sx={{ display: 'flex', gap: 1, py: 2, position: 'sticky', top: '52px', zIndex: 2, bgcolor: 'background.paper', flexWrap: 'wrap' }}>
        {CLIENT_SCENARIOS.map((s, i) => (
          <Button
            key={s.key}
            size="small"
            variant={activeIndex === i ? 'contained' : 'outlined'}
            onClick={() => setActiveIndex(i)}
          >
            {s.label}
          </Button>
        ))}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button
            size="small"
            startIcon={reportExporting ? <CircularProgress size={14} /> : <FileDownloadIcon />}
            onClick={handleExportReport}
            disabled={reportExporting || dailyNormsState.goalsLoading}
          >
            Звіт по клієнту
          </Button>
          <Button size="small" startIcon={<DescriptionIcon />} onClick={openConfigurator}>
            Налаштувати симулянтів
          </Button>
        </Box>
      </Box>

      <BaseDialog
        open={configuratorOpen}
        onClose={() => setConfiguratorOpen(false)}
        title="Конфігуратор симулянтів"
        maxWidth="md"
        actions={<Button variant="contained" onClick={applyJsonDraft}>Застосувати</Button>}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Сирий JSON усіх {profiles.length} профілів — редагуй прямо тут або довантаж/збережи
          файлом. Діє лише в пам'яті цієї сесії (не пишеться в `health-index-client-scenarios.json`
          на диску) — "Скинути до дефолту" повертає саме до того файлу.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small" startIcon={<FileDownloadIcon />} onClick={handleExportProfiles}>
            Зберегти JSON
          </Button>
          <Button size="small" startIcon={<FileUploadIcon />} onClick={() => importClientsInputRef.current?.click()}>
            Завантажити JSON
          </Button>
          <input
            ref={importClientsInputRef}
            type="file"
            accept="application/json"
            hidden
            onChange={handleImportProfiles}
          />
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={() => {
              const defaults = CLIENT_SCENARIOS.map((s) => s.profile);
              setJsonDraft(JSON.stringify(defaults, null, 2));
              setJsonDraftError(null);
            }}
          >
            Скинути до дефолту
          </Button>
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={16}
          maxRows={28}
          value={jsonDraft}
          onChange={(e) => setJsonDraft(e.target.value)}
          error={!!jsonDraftError}
          helperText={jsonDraftError}
          sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
      </BaseDialog>


      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PHASE 0 — Intro → Disclaimer → Registration</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          {/* Реальні скріни з Figma (2026-08-21), точні layer-назви з файлу — за проханням
              користувача "будемо прив'язуватися до імен скринів Figma", "треба точно підписувати":
              node-id 2004-9852 → page:onboarding:intro, node-id 2006-14195 → page:onboarding:disclaimer. */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box component="img" src="/onboarding/phase0-intro.png" alt="page:onboarding:intro" sx={{ width: 80, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
              <PageNameTag name="page:onboarding:intro" sx={{ mt: 0.5 }} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box component="img" src="/onboarding/phase0-disclaimer.png" alt="page:onboarding:disclaimer" sx={{ width: 80, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
              <PageNameTag name="page:onboarding:disclaimer" sx={{ mt: 0.5 }} />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <LogicPanel>
            <Typography variant="body2">
              Тут нічого не розраховується
            </Typography>
          </LogicPanel>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PHASE 1 — Starting Health Index (6 self-assessment)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>

      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Phase1Card scenario={scenario} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Phase1LogicPanel scenario={scenario} />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PHASE 1.1 — Focus</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {/* Тимчасово закоментовано (2026-08-26, за проханням користувача). <SpecQuote quoteKey="focusSelect" /> */} 5 варіантів, без "ясність" — буквально за задачею.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Phase1_1Card scenario={scenario} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Phase1_1LogicPanel />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PHASE 2 — Goal + базова персоналізація</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Редаговані поля зліва — це саме те, що годує `calculated-metrics` (BMR/AMR/WDNA).{' '}
        {/* Тимчасово закоментовано (2026-08-26, за проханням користувача). <SpecQuote quoteKey="goal" /> */} Результат справа — {/* <SpecQuote quoteKey="personalPlan" /> */}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <ProfileCard
            label={scenario.label}
            profile={profiles[activeIndex]}
            onChange={(next) => setProfiles((prev) => prev.map((p, idx) => (idx === activeIndex ? next : p)))}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <Phase2ResultCard profile={profiles[activeIndex]} {...dailyNormsState} />
        </Grid>
      </Grid>

      {/* Підсумок після PHASE 2 (2026-08-21, за проханням користувача — "резюмуємо які дані ми
          зібрали та які цілі отримали", "далі йде вже необов'язкові характеристики"): PHASE 0-2 —
          обов'язковий, синхронний блок збору даних для Стартового Індексу й Персонального плану;
          PHASE 3/4 (нижче) — опційні, не блокують перший вхід у застосунок і не впливають на
          базову функціональність з точки зору MVP. Divider + відступ — явний чекпоінт між ними. */}
      <Divider sx={{ mb: 3 }} />
      <Phase2SummaryBlock scenario={scenario} profile={profiles[activeIndex]} {...dailyNormsState} />
      {/* Явний маркер "усе нижче — не готовий, опційний функціонал" (2026-08-21, за проханням
          користувача — "після цього блоку великими буквами ДАЛІ У РОЗРОБЦІ"). */}
      <Typography variant="h6" fontWeight={800} color="text.secondary" align="center" sx={{ mt: 3, letterSpacing: 1 }}>
        ДАЛІ У РОЗРОБЦІ
      </Typography>
      <Divider sx={{ mb: 3, mt: 1 }} />

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PHASE 3 — Progressive profile completion — {scenario.label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {/* Тимчасово закоментовано (2026-08-26, за проханням користувача).
        <SpecQuote quoteKey="phase3List" /> <SpecQuote quoteKey="favoriteActivities" />
        */}
      </Typography>

      {/* Незалежні блоки PHASE 3 (2026-08-20) — кожне поле показується користувачу окремо, в
          різний час (task.md: "не робити жорсткий сценарій «день 1/день 2/день 3»") — тому й тут
          кожне поле свій власний 2-колоночний блок, а не спільна картка на всі 6 полів. Сон —
          перший блок, витягнутий з колишньої спільної Phase3Card/Phase3LogicPanel як прецедент. */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Phase3SleepCard scenario={scenario} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Phase3SleepLogicPanel scenario={scenario} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Phase3Card scenario={scenario} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Phase3LogicPanel />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>PHASE 4 — Optional / contextual</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {/* Тимчасово закоментовано (2026-08-26, за проханням користувача). <SpecQuote quoteKey="phase4List" /> */}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Phase4Card scenario={scenario} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Phase4LogicPanel />
        </Grid>
      </Grid>
    </Box>
  );
};

// ---- Таб "Вхідні дані" — 3 групи + спільна модалка результату ----

const InputsTab: React.FC<{ config: HealthIndexConfig }> = ({ config }) => {
  const [result, setResult] = useState<ResultState>(null);
  const baselineRef = useRef<BaselineIndexGroupHandle>(null);
  const dailyRef = useRef<DailyResourceGroupHandle>(null);
  const rollingRef = useRef<RollingIndexGroupHandle>(null);

  const openFilledQuestionnaire = () => {
    if (!baselineRef.current || !dailyRef.current || !rollingRef.current) return;
    openQuestionnaireDocument(config, {
      baseline: baselineRef.current.getSnapshot(),
      daily: dailyRef.current.getSnapshot(),
      rolling: rollingRef.current.getSnapshot(),
    });
  };

  // Для тестування (2026-08-21) — через наявний механізм статичних сторінок
  // (GET /information/pages/:page, @Public()), не окремий ендпоінт під EHI. Відкривається
  // в модалці (BaseDialog), як прийнято в проєкті для таких сторінок з бекенду (напр. Умови
  // використання/Політика конфіденційності в RegisterForm.tsx), а не window.open в нову вкладку.
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [explanationHtml, setExplanationHtml] = useState<string | null>(null);
  const [explanationLoading, setExplanationLoading] = useState(false);

  const openExplanation = async () => {
    setExplanationOpen(true);
    if (explanationHtml) return;
    setExplanationLoading(true);
    try {
      const response = await api.get('/information/pages/health_index_explanation', { headers: { Accept: 'text/html' } });
      setExplanationHtml(response.data);
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося завантажити пояснення.'));
      setExplanationOpen(false);
    } finally {
      setExplanationLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<InfoOutlinedIcon />} variant="outlined" onClick={openExplanation}>
          Пояснення користувачу (оригінал ТЗ)
        </Button>
        <Button startIcon={<DescriptionIcon />} variant="outlined" onClick={openFilledQuestionnaire}>
          Анкета з поточними даними (приклад)
        </Button>
        <Button startIcon={<DescriptionIcon />} variant="outlined" onClick={() => openQuestionnaireDocument(config)}>
          Анкета для заповнення (HTML)
        </Button>
      </Box>

      <BaseDialog open={explanationOpen} onClose={() => setExplanationOpen(false)} title="Пояснення користувачу (оригінал ТЗ)">
        {explanationLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          explanationHtml && <div dangerouslySetInnerHTML={{ __html: explanationHtml }} />
        )}
      </BaseDialog>

      <BaselineIndexGroup ref={baselineRef} config={config} onResult={(response) => setResult({ calculator: 'baseline-index', response })} />
      <Divider sx={{ my: 3 }} />
      <DailyResourceGroup ref={dailyRef} config={config} onResult={(response) => setResult({ calculator: 'daily-resource', response })} />
      <Divider sx={{ my: 3 }} />
      <RollingIndexGroup ref={rollingRef} config={config} onResult={(response) => setResult({ calculator: 'rolling-index', response })} />

      <ResultModal state={result} onClose={() => setResult(null)} />
    </Box>
  );
};

// ---- Таб "Конфігуратор" ----

const ConfiguratorTab: React.FC<{
  config: HealthIndexConfig;
  defaultConfig: HealthIndexConfig;
  onChange: (next: HealthIndexConfig) => void;
  onSaved: () => Promise<void>;
}> = ({ config, defaultConfig, onChange, onSaved }) => {
  const importInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);

  // Зберегти на сервер - `system-settings.health_index_config` (рішення користувача 2026-09-23: "запакувати
  // все в єдиний конфіг щоби адміністратори могли коригувати коефіцієнти... без втручання розробників").
  // Генеричний `POST /admin/system-settings/:key` (AI-промпти з 2026-09-30 - окремо, `/admin/system-prompts`) -
  // кеш у пам'яті на бекенді (`SystemSettingsService`), без нової таблиці й ендпоінта. `onSaved` - перечитати
  // конфіг з сервера після збереження, щоб "Скинути" відображав щойно збережене, а не застарілий знімок.
  const handleSaveToServer = async () => {
    setSaving(true);
    try {
      await api.post('/admin/system-settings/health_index_config', {
        value: config,
        description: 'Конфіг Everyday Health Index (коефіцієнти, пороги, вікна) - редагується через /admin/health-index?tab=configurator',
      });
      await onSaved();
      toastService.success('Конфіг збережено - тепер це реальні значення розрахунку EHI.');
    } catch (err) {
      toastService.error(getErrorMessage(err, 'Не вдалося зберегти конфіг на сервері'));
    } finally {
      setSaving(false);
    }
  };

  const handleExportConfig = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `health-index-config-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportConfig = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!parsed || typeof parsed !== 'object' || !parsed.sixDirectionsMatrix) {
          throw new Error('Не схоже на конфіг Health Index (немає sixDirectionsMatrix)');
        }
        onChange(parsed as HealthIndexConfig);
        toastService.success('Конфіг завантажено з файлу');
      } catch (err) {
        toastService.error(`Не вдалося прочитати JSON: ${getErrorMessage(err, 'невідома помилка')}`);
      }
    };
    reader.readAsText(file);
  };

  const updateMatrixCell = (direction: keyof HealthIndexConfig['sixDirectionsMatrix'], block: string, value: number) => {
    onChange({
      ...config,
      sixDirectionsMatrix: {
        ...config.sixDirectionsMatrix,
        [direction]: { ...config.sixDirectionsMatrix[direction], [block]: value },
      },
    });
  };

  const updateIndexLevel = (index: number, patch: Partial<IndexLevelRow>) => {
    const next = [...config.indexLevels];
    next[index] = { ...next[index], ...patch };
    onChange({ ...config, indexLevels: next });
  };

  const updateBaselineStatus = (index: number, patch: Partial<BaselineStatusRow>) => {
    const next = [...config.baselineStatuses];
    next[index] = { ...next[index], ...patch };
    onChange({ ...config, baselineStatuses: next });
  };

  const columnSums = useMemo(
    () =>
      BLOCKS.reduce<Record<string, number>>((acc, block) => {
        acc[block] = DIRECTIONS.reduce((sum, d) => sum + (config.sixDirectionsMatrix[d] as any)[block], 0);
        return acc;
      }, {}),
    [config],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          "Зберегти на сервері" робить ці значення РЕАЛЬНИМ конфігом EHI (для всіх користувачів, одразу) - до того
          зміни діють лише в цій сесії редагування. Ваги довільні, не виведені емпірично — <SourceRefs orig="5, 18" spec="17 (п. 3)" />
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveToServer} disabled={saving}>
            {saving ? 'Зберігаю…' : 'Зберегти на сервері'}
          </Button>
          <Button size="small" startIcon={<FileDownloadIcon />} onClick={handleExportConfig}>
            Зберегти JSON
          </Button>
          <Button size="small" startIcon={<FileUploadIcon />} onClick={() => importInputRef.current?.click()}>
            Завантажити JSON
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json"
            hidden
            onChange={handleImportConfig}
          />
          <Button size="small" startIcon={<RestartAltIcon />} onClick={() => onChange(defaultConfig)}>
            Скинути до активного
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <ParamLabel label="Матриця 6 напрямків" param="sixDirectionsMatrix" quoteKey="sixDirectionsMatrix" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Кожен рядок має сумувати до 1,00; кожна колонка (сума ваги блоку по всіх напрямках) — до 1,20. Інакше
        середнє(6 напрямків) розійдеться з Ресурсом дня.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Напрямок (direction)</TableCell>
              {BLOCKS.map((b) => (
                <TableCell key={b}>{BLOCK_LABELS[b]} ({b})</TableCell>
              ))}
              <TableCell>Сума рядка</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {DIRECTIONS.map((d) => {
              const weights = config.sixDirectionsMatrix[d] as any;
              const rowSum = BLOCKS.reduce((sum, b) => sum + weights[b], 0);
              return (
                <TableRow key={d}>
                  <TableCell>
                    {DIRECTION_LABELS[d]}{' '}
                    <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>({d})</Typography>{' '}
                    <SpecQuote quoteKey={d} />
                  </TableCell>
                  {BLOCKS.map((b) => (
                    <TableCell key={b}>
                      <TextField
                        type="number"
                        inputProps={{ step: 0.01 }}
                        value={weights[b]}
                        onChange={(e) => updateMatrixCell(d, b, Number(e.target.value))}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <Chip
                      size="small"
                      label={rowSum.toFixed(2)}
                      color={Math.abs(rowSum - 1) <= 0.001 ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Сума колонки</TableCell>
              {BLOCKS.map((b) => (
                <TableCell key={b}>
                  <Chip
                    size="small"
                    label={columnSums[b].toFixed(2)}
                    color={Math.abs(columnSums[b] - 1.2) <= 0.001 ? 'success' : 'warning'}
                    variant="outlined"
                  />
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <ParamLabel label="Рівні Індексу" param="indexLevels" quoteKey="indexLevels" />
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Код (level)</TableCell>
              <TableCell>Підпис (label)</TableCell>
              <TableCell>Від, включно (min)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {config.indexLevels.map((row, i) => (
              <TableRow key={row.level}>
                <TableCell>{row.level}</TableCell>
                <TableCell>
                  <TextField value={row.label} onChange={(e) => updateIndexLevel(i, { label: e.target.value })} sx={{ minWidth: 180 }} />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={row.min}
                    onChange={(e) => updateIndexLevel(i, { min: Number(e.target.value) })}
                    sx={{ width: 90 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <ParamLabel label="Вікна й шкала оцінки" param="rollingWindowDays / baselineWindowDays / ratingScale" quoteKey="ratingScale" />
      </Box>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={6} sm={3}>
          <NotActiveInEhi>
            <TextField
              label="Вікно Індексу, днів (rollingWindowDays)"
              type="number"
              value={config.rollingWindowDays}
              onChange={(e) => onChange({ ...config, rollingWindowDays: Number(e.target.value) })}
              fullWidth
            />
          </NotActiveInEhi>
        </Grid>
        <Grid item xs={6} sm={3}>
          <NotActiveInEhi>
            <TextField
              label="Вікно baseline, днів (baselineWindowDays)"
              type="number"
              value={config.baselineWindowDays}
              onChange={(e) => onChange({ ...config, baselineWindowDays: Number(e.target.value) })}
              fullWidth
            />
          </NotActiveInEhi>
        </Grid>
        <Grid item xs={4} sm={2}>
          <TextField
            label="Оцінка: мін (ratingScale.min)"
            type="number"
            value={config.ratingScale.min}
            onChange={(e) => onChange({ ...config, ratingScale: { ...config.ratingScale, min: Number(e.target.value) } })}
            fullWidth
          />
        </Grid>
        <Grid item xs={4} sm={2}>
          <TextField
            label="Оцінка: макс (ratingScale.max)"
            type="number"
            value={config.ratingScale.max}
            onChange={(e) => onChange({ ...config, ratingScale: { ...config.ratingScale, max: Number(e.target.value) } })}
            fullWidth
          />
        </Grid>
        <Grid item xs={4} sm={2}>
          <TextField
            label="Крок, % (ratingScale.stepPercent)"
            type="number"
            value={config.ratingScale.stepPercent}
            onChange={(e) => onChange({ ...config, ratingScale: { ...config.ratingScale, stepPercent: Number(e.target.value) } })}
            fullWidth
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <ParamLabel label="Пуш про зміну EHI" param="ehiChangeNotificationThreshold" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Мінімальна зміна відображуваного EHI (день до дня, ціле число), щоб надіслати користувачу пуш "EHI змінився".
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Поріг, балів (ehiChangeNotificationThreshold)"
            type="number"
            inputProps={{ step: 1, min: 1 }}
            value={config.ehiChangeNotificationThreshold}
            onChange={(e) => onChange({ ...config, ehiChangeNotificationThreshold: Math.round(Number(e.target.value)) })}
            fullWidth
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <ParamLabel label="Коридор N (харчування) і S (сон)" param="nutritionCorridor / sleepCorridor" quoteKey="corridor" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        100 балів у [fullRangeMin, fullRangeMax] (частка факт/ціль), лінійний спад до 0 на межах
        [zeroBelow, zeroAbove]. Продукт явно лишає ці межі відкритим питанням. <SourceRefs orig="18" spec="16" />
      </Typography>
      <Grid container spacing={2}>
        {(['nutritionCorridor', 'sleepCorridor'] as const).map((key) => {
          const fields = (
            <>
              <ParamLabel label={key === 'nutritionCorridor' ? 'N — харчування' : 'S — сон'} param={key} color={key === 'nutritionCorridor' ? 'success' : 'primary'} quoteKey={key === 'nutritionCorridor' ? 'N' : 'S'} />
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                {(['zeroBelow', 'fullRangeMin', 'fullRangeMax', 'zeroAbove'] as const).map((field) => (
                  <TextField
                    key={field}
                    label={field}
                    type="number"
                    inputProps={{ step: 0.05 }}
                    value={config[key][field]}
                    onChange={(e) => onChange({ ...config, [key]: { ...config[key], [field]: Number(e.target.value) } })}
                    sx={{ width: 110 }}
                  />
                ))}
              </Box>
            </>
          );
          return (
            <Grid item xs={12} sm={6} key={key}>
              {key === 'sleepCorridor' ? <NotActiveInEhi>{fields}</NotActiveInEhi> : fields}
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 3 }}>
        <NotActiveInEhi>
          <ParamLabel label="Підписи періоду Стартового Індексу" param="baselineStatuses" quoteKey="baselineStatuses" />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <code>{'{n}'}</code> у підписі підставляється сервером кількістю завершених днів. Останній рядок ("full") діє для
            решти — його межу редагувати не можна.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Статус (status)</TableCell>
                  <TableCell>Підпис (label)</TableCell>
                  <TableCell>До скількох днів, включно (maxDays)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {config.baselineStatuses.map((row, i) => (
                  <TableRow key={row.status}>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>
                      <TextField
                        value={row.label}
                        onChange={(e) => updateBaselineStatus(i, { label: e.target.value })}
                        sx={{ minWidth: 260 }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.maxDays === null ? (
                        <Typography variant="body2" color="text.secondary">решта</Typography>
                      ) : (
                        <TextField
                          type="number"
                          value={row.maxDays}
                          onChange={(e) => updateBaselineStatus(i, { maxDays: Number(e.target.value) })}
                          sx={{ width: 90 }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </NotActiveInEhi>
      </Box>
    </Box>
  );
};

// ---- Головна сторінка ----

// Єдина сторінка всього, що стосується EHI (2026-09-22, за проханням користувача): раніше журнал
// (/admin/ehi-journal - "Узагальнені", "Знімки") і калькулятор (/admin/health-index - "Симуляція",
// "Вхідні дані", "Конфігуратор") були окремими сторінками. Логіка вкладок не змінювалась - сторінка
// лише рендерить наявні компоненти в одному наборі вкладок. /admin/ehi-journal тепер редірект сюди
// (AdminEhiJournalRedirect в routes.tsx), зберігаючи ?tab=. Порядок - за вказівкою користувача:
// журнал першим ("Узагальнені" - головна вкладка), далі конфігуратор, симуляція, вхідні дані.
const DEFAULT_TAB = 'summaries';
// Для тих, кому журнал недоступний (див. нижче), - перша доступна вкладка за тим самим порядком.
const DEFAULT_TAB_WITHOUT_JOURNAL = 'configurator';
const TABS_STORAGE_KEY = 'tab:admin-health-index';

const JOURNAL_TABS = new Set(['summaries', 'snapshots']);
const CALCULATOR_TABS = new Set(['configurator', 'simulation', 'inputs']);

const TABS: AdaptiveTabItem[] = [
  // Підписи вкладок журналу - ключі i18n (як були на сторінці журналу), решта - як були в калькуляторі.
  { value: 'summaries', label: 'ehiJournal.tabSummaries', iDoc: TabIndices.ADMIN_EHI_SUMMARIES },
  { value: 'snapshots', label: 'ehiJournal.tabSnapshots', iDoc: TabIndices.ADMIN_EHI_SNAPSHOTS },
  { value: 'configurator', label: 'Конфігуратор', iDoc: TabIndices.ADMIN_HEALTH_INDEX_CONFIGURATOR },
  { value: 'simulation', label: 'Симуляція', iDoc: TabIndices.ADMIN_HEALTH_INDEX_SIMULATION },
  { value: 'inputs', label: 'Вхідні дані', iDoc: TabIndices.ADMIN_HEALTH_INDEX_INPUTS },
];

const AdminHealthIndexCalculatorPageContent: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  // Вкладки журналу - для superadmin/admin/developer.
  const canSeeJournal = (user?.roles ?? []).some((role) => ['superadmin', 'admin', 'developer'].includes(role));
  const visibleTabs: AdaptiveTabItem[] = TABS.map((tab) => {
    const translated = JOURNAL_TABS.has(tab.value) ? { ...tab, label: t(tab.label) } : tab;
    if (JOURNAL_TABS.has(tab.value) && !canSeeJournal) return { ...translated, disabled: true };
    return translated;
  });
  const defaultTab = canSeeJournal ? DEFAULT_TAB : DEFAULT_TAB_WITHOUT_JOURNAL;
  const currentTab = useAdaptiveTab(visibleTabs, { searchParamKey: 'tab', defaultValue: defaultTab, storageKey: TABS_STORAGE_KEY });

  const [defaultConfig, setDefaultConfig] = useState<HealthIndexConfig | null>(null);
  const [config, setConfig] = useState<HealthIndexConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // `defaultConfig` - те, що РЕАЛЬНО зараз активне (збережений в system-settings адмінський override поверх
  // хардкоду, `HealthIndexService.getConfig()` на бекенді), а не голий заводський хардкод (рішення користувача
  // 2026-09-23). Винесено в callback, щоб перевикликати після "Зберегти на сервері" в конфігураторі - "Скинути"
  // тоді скидає до щойно збереженого, а не до застарілого знімку з моменту відкриття сторінки.
  const loadConfig = useCallback(async (): Promise<void> => {
    try {
      const info = await getCalculatorInfo('daily-resource');
      setDefaultConfig(info.defaultConfig);
      setConfig(info.defaultConfig);
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося завантажити конфіг Everyday Health Index.'));
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Конфіг потрібен лише вкладкам калькулятора - вкладки журналу його не чекають (раніше на окремій
  // сторінці журналу цього запиту не було взагалі). Конфіг вантажиться один раз на сторінку, як і раніше:
  // перемикання між вкладками його не перезапитує і не скидає зміни в конфігураторі.
  const configReady = !loadingConfig && !!config && !!defaultConfig;

  return (
    <Box data-i-doc={PageIndices.ADMIN_HEALTH_INDEX}>
      <PageHint iDoc={PageIndices.ADMIN_HEALTH_INDEX} />

      {/* Вкладки над контентом, не в картці: вкладки журналу мають власні PageCard (з PageToolbar), тож
          спільна зовнішня картка дала б "картку в картці". Вкладки калькулятора - у своїй PageCard, як і раніше. */}
      <AdaptiveTabs tabs={visibleTabs} searchParamKey="tab" defaultValue={defaultTab} storageKey={TABS_STORAGE_KEY} />

      <Box sx={{ pt: 2 }}>
        {currentTab === 'summaries' && <EhiSummariesTab />}
        {currentTab === 'snapshots' && <EhiSnapshotsTab />}

        {CALCULATOR_TABS.has(currentTab) && (
          configReady ? (
            <PageCard>
              {currentTab === 'inputs' && <InputsTab config={config} />}
              {currentTab === 'simulation' && <SimulationTab />}
              {currentTab === 'configurator' && (
                <ConfiguratorTab config={config} defaultConfig={defaultConfig} onChange={setConfig} onSaved={loadConfig} />
              )}
            </PageCard>
          ) : (
            <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
              {loadingConfig && <CircularProgress />}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export const AdminHealthIndexCalculatorPage: React.FC = () => (
  <DashboardLayout>
    <AdminHealthIndexCalculatorPageContent />
  </DashboardLayout>
);

export default AdminHealthIndexCalculatorPage;
